<script>
import { mapState, mapGetters } from "vuex";
import { useAlert } from "dashboard/composables";
import Input from "dashboard/components-next/input/Input.vue";
import Modal from "dashboard/components/Modal.vue";
import Select from "dashboard/components/crm/Select.vue";
import SearchableSelect from "dashboard/components/crm/SearchableSelect.vue";
import SearchableContactSelect from "dashboard/components/crm/SearchableContactSelect.vue";
import CrmMultiSelectSearch from "dashboard/components/crm/CrmMultiSelectSearch.vue";
import StageColumn from "dashboard/components/crm/StageColumn.vue";
import DealFilters from "dashboard/components/crm/DealFilters.vue";
import DealStageMover from "dashboard/components/crm/DealStageMover.vue";
import StageFormBuilder from "dashboard/components/crm/StageFormBuilder.vue";
import DealStageAttributes from "dashboard/components/crm/DealStageAttributes.vue";
import CRMPipelinesAPI from "dashboard/api/crm/pipelines.js";
import CRMStagesAPI from "dashboard/api/crm/stages.js";
import CRMDealsAPI from "dashboard/api/crm/deals.js";
import CRMActivitiesAPI from "dashboard/api/crm/activities.js";
import CRMProductsAPI from "dashboard/api/crm/products.js";
import TeamsAPI from "dashboard/api/teams.js";
import ContactAPI from "dashboard/api/contacts.js";
import {
  getUnixStartOfDay,
  getUnixEndOfDay,
  formatUnixDate,
} from "helpers/DateHelper";

export default {
  name: "CrmBoardView",
  components: {
    Input,
    Modal,
    Select,
    StageColumn,
    DealFilters,
    DealStageMover,
    StageFormBuilder,
    DealStageAttributes,
    SearchableSelect,
    SearchableContactSelect,
    CrmMultiSelectSearch,
  },
  data() {
    return {
      isMobile: window.innerWidth < 768,
      activeMobileStageIndex: 0,
      _resizeTimer: null,
      // Mobile rediseño: menús, buscador y bottom sheet de filtros
      showMobilePipelineMenu: false,
      showMobileHeaderMenu: false,
      showMobileFilterSheet: false,
      showMobileStageSheet: false,
      mobileQuery: "",
      _mobileSearchTimer: null,
      mobileFilterDraft: {
        ownerIds: [],
        dateFrom: "",
        dateTo: "",
        amountMin: null,
        amountMax: null,
      },
      selectedPipeline: this.$route.query.pipeline_id
        ? parseInt(this.$route.query.pipeline_id)
        : null,
      showNewPipelineForm: false,
      showNewStageForm: false,
      showPipelineMenu: false,
      showDeleteConfirmationModal: false,
      isEditingPipeline: false,
      pipelineForm: {
        name: "",
        deals_visibility: "team", // valor por defecto
        team_ids: [], // equipos seleccionados
      },
      stageForm: {
        name: "",
        probability: 0,
        position: null, // Para edición mantendrá la posición actual
        stage_type: "regular",
        form_config: {
          enabled: false,
          fields: [],
        },
      },
      stageNameError: "",
      stageProbabilityError: "",
      dealTitleError: "",
      dealAmountError: "",
      pipelineNameError: "",
      deleteConfirmation: {
        stats: null,
        confirmationText: "",
        isLoadingStats: false,
      },
      showDeleteStageModal: false,
      stageToDelete: null,
      isEditingStage: false,
      isDeletingStage: false,
      isCreatingPipeline: false,
      isDeletingPipeline: false,
      isCreatingStage: false,

      // Deal modal properties
      showNewDealForm: false,
      showEditDealForm: false,
      showDeleteConfirmation: false,
      isCreatingDeal: false,
      isUpdatingDeal: false,
      isDeletingDeal: false,
      _loadedProductIds: null, // IDs de productos cargados del backend (evita recalcular al cargar el form de edición)
      dealForm: {
        title: "",
        pipeline_id: null,
        stage_id: null,
        owner_id: null,
        primary_contact_id: null,
        conversation_id: null,
        amount: "",
        currency: "USD",
        expected_close_date: "",
        product_ids: [], // Array para soportar múltiples productos
        phone: "",
        contact_ids: [],
        payment_method_ids: [], // Array para soportar múltiples métodos de pago
        labels: [], // FORK: etiquetas del deal (array de {id,name} en el form)
      },
      contactConversations: [],
      isLoadingConversations: false,
      editingDealId: null,
      // FORK: deal cargado vía deep-link (?deal_id=) para abrir el modal sin depender
      // del board. Sirve de fallback en editingDeal mientras fetchBoard repuebla dealsById.
      routeDeal: null,

      // Activities modal properties
      showCreateActivityModal: false,
      selectedDealForActivities: null,
      isCreatingActivity: false,
      activityForm: {
        activity_type: "task",
        subject: "",
        description: "",
        due_at: "",
        owner_id: null,
        deal_id: null,
      },

      // Activity detail modal properties
      showActivityDetailModal: false,
      selectedActivity: null,
      selectedDealForActivityDetail: null,

      // Cambio de etapa (drag y modal de edición comparten un único
      // DealStageMover; ver handleDealMoveFormRequired/handleDealMoved).
      // Rastrea si el movimiento en curso pasó por el formulario del stage,
      // para solo alertar éxito cuando hubo un paso explícito del usuario.
      pendingMoveShowedForm: false,

      // Dropdown data
      availableStages: [],
      availableAgents: [],
      availableProducts: [],
      availableCurrencies: [
        { code: "USD", name: "Dólar Estadounidense" },
        { code: "ARS", name: "Peso Argentino" },
      ],
    };
  },
  computed: {
    ...mapState("crmBoard", [
      "stagesById",
      "stageOrder",
      "pipelines",
      "pipeline",
      "uiFlags",
    ]),
    ...mapGetters(["getCurrentUser"]),
    ...mapGetters("teams", ["getTeams"]),
    ...mapGetters("crmBoard", { totals: "pipelineTotals" }),
    ...mapGetters("crmBoard", [
      "columnTotals",
      "columnWeightedTotals",
      "filteredColumnDeals",
      "pipelineOwners",
      "hasActiveFilters",
    ]),
    ...mapGetters("labels", ["getLabelsByType"]),
    // FORK: opciones de etiquetas para deals (id = título, como espera CrmMultiSelectSearch).
    dealLabelOptions() {
      return this.getLabelsByType("deal").map((label) => ({
        id: label.title,
        name: label.title,
      }));
    },
    accountId() {
      return this.$route.params.accountId;
    },
    // ===== Mobile: etapa activa y derivados =====
    activeStageId() {
      return this.stageOrder[this.activeMobileStageIndex] ?? this.stageOrder[0];
    },
    activeStage() {
      return this.activeStageId ? this.stagesById[this.activeStageId] : null;
    },
    // Posición real en stageOrder (activeMobileStageIndex puede quedar fuera de
    // rango tras borrar una etapa, en cuyo caso activeStageId cae a la primera).
    activeStagePosition() {
      return this.stageOrder.findIndex(
        (id) => Number(id) === Number(this.activeStageId),
      );
    },
    activeStageDeals() {
      return this.activeStageId
        ? this.filteredColumnDeals(this.activeStageId)
        : [];
    },
    activeStageLoadedCount() {
      return this.activeStageId
        ? (this.$store.state.crmBoard.dealsByStageId[this.activeStageId] || [])
            .length
        : 0;
    },
    activeStageMoneyTotals() {
      return this.activeStageId
        ? this.columnTotals(this.activeStageId).sumByCurrency
        : {};
    },
    activeStageWeightedTotals() {
      return this.activeStageId
        ? this.columnWeightedTotals(this.activeStageId).weightedSumByCurrency
        : {};
    },
    // Chips de filtros activos para la fila scrolleable
    mobileFilterChips() {
      const f = this.$store.state.crmBoard.filters;
      const chips = [];
      if (f.query) chips.push({ key: "query", label: `"${f.query}"` });
      if (f.ownerIds?.length)
        chips.push({
          key: "owner",
          label:
            f.ownerIds.length === 1
              ? "1 responsable"
              : `${f.ownerIds.length} responsables`,
        });
      if (f.createdAtFrom || f.createdAtTo)
        chips.push({ key: "date", label: "Fecha" });
      if (f.amountMin != null || f.amountMax != null)
        chips.push({ key: "amount", label: "Monto" });
      return chips;
    },
    isBoardLoading() {
      return this.uiFlags?.isFetching || false;
    },
    isAdmin() {
      return this.getCurrentUser?.role === "administrator";
    },
    canDeleteDeal() {
      if (!this.editingDeal || !this.getCurrentUser) return false;

      // Admin puede eliminar cualquier deal
      if (this.isAdmin) return true;

      // El creador puede eliminar su propio deal
      return this.editingDeal.created_by_id === this.getCurrentUser.id;
    },
    // Formatear etapas con probabilidad para el componente Select
    availableStagesFormatted() {
      return this.availableStages.map((stage) => ({
        id: stage.id,
        name: `${stage.name} (${stage.probability}%)`,
      }));
    },
    // Formatear monedas con código y nombre para el componente Select
    availableCurrenciesFormatted() {
      return this.availableCurrencies.map((currency) => ({
        code: currency.code,
        name: `${currency.code} - ${currency.name}`,
      }));
    },
    // Opciones de tipo de etapa para el componente Select
    stageTypeOptions() {
      return [
        { value: "regular", name: this.$t("CRM.STAGE_TYPES.REGULAR") },
        { value: "won", name: this.$t("CRM.STAGE_TYPES.WON") },
        { value: "lost", name: this.$t("CRM.STAGE_TYPES.LOST") },
        { value: "canceled", name: this.$t("CRM.STAGE_TYPES.CANCELED") },
        { value: "paused", name: this.$t("CRM.STAGE_TYPES.PAUSED") },
      ];
    },
    currentPipeline() {
      // Asegurar reactividad cuando cambien los pipelines
      if (
        !this.selectedPipeline ||
        !this.pipelines ||
        this.pipelines.length === 0
      ) {
        return null;
      }

      return this.pipelines.find((p) => p.id === this.selectedPipeline) || null;
    },
    shouldShowPipelineMenu() {
      return this.isAdmin && this.currentPipeline && this.selectedPipeline;
    },

    // Computed properties para productos múltiples
    selectedProducts() {
      // Asegurar que product_ids siempre sea un array
      const productIds = Array.isArray(this.dealForm.product_ids)
        ? this.dealForm.product_ids
        : [];
      if (productIds.length === 0) return [];

      // Extraer IDs de objetos {id, name} del MultiSelect
      const ids = productIds.map((item) =>
        typeof item === "object" ? item.id : item,
      );
      return this.availableProducts.filter((p) => ids.includes(p.id));
    },

    selectedProductsCurrency() {
      if (this.selectedProducts.length === 0) return null;
      return this.selectedProducts[0].currency;
    },

    hasProductsSelected() {
      return this.selectedProducts.length > 0;
    },

    calculatedAmountFromProducts() {
      if (!this.hasProductsSelected) return 0;
      return this.selectedProducts.reduce(
        (sum, product) => sum + (product.price_cents || 0),
        0,
      );
    },

    filteredProductsByCompatibleCurrency() {
      // Si no hay productos seleccionados, mostrar todos
      if (!this.hasProductsSelected) {
        return this.availableProducts.map((p) => ({
          id: p.id,
          name: p.name + (p.formatted_price ? " - " + p.formatted_price : ""),
        }));
      }

      // Si hay productos seleccionados, filtrar por la misma moneda
      const selectedCurrency = this.selectedProductsCurrency;
      return this.availableProducts
        .filter((p) => p.currency === selectedCurrency)
        .map((p) => ({
          id: p.id,
          name: p.name + (p.formatted_price ? " - " + p.formatted_price : ""),
        }));
    },

    // Opciones de métodos de pago (enum values from backend)
    paymentMethodOptions() {
      return [
        { id: 0, name: "Transferencia bancaria", value: "bank_transfer" },
        { id: 1, name: "Efectivo", value: "cash" },
        { id: 2, name: "Tarjeta de débito", value: "debit_card" },
        { id: 3, name: "Tarjeta de crédito", value: "credit_card" },
        { id: 4, name: "QR", value: "qr_code" },
        { id: 5, name: "Cheque electrónico", value: "electronic_check" },
      ];
    },

    isAmountReadonly() {
      return this.hasProductsSelected;
    },

    isCurrencyReadonly() {
      return this.hasProductsSelected;
    },

    editingDeal() {
      if (!this.editingDealId) return null;
      return (
        this.$store.state.crmBoard.dealsById[this.editingDealId] ||
        this.routeDeal ||
        null
      );
    },
    formConfigJson: {
      get() {
        if (!this.stageForm || !this.stageForm.form_config) {
          return "[]";
        }
        return JSON.stringify(this.stageForm.form_config.fields || [], null, 2);
      },
      set(value) {
        try {
          if (!this.stageForm.form_config) {
            this.stageForm.form_config = { enabled: false, fields: [] };
          }
          this.stageForm.form_config.fields = JSON.parse(value);
        } catch (e) {
          // Mantener valor anterior si JSON inválido
        }
      },
    },
  },
  watch: {
    "stageForm.stage_type"(newType) {
      if (newType === "won") {
        this.stageForm.probability = 100;
      } else if (newType === "lost" || newType === "canceled") {
        this.stageForm.probability = 0;
      }
    },
    selectedPipeline: {
      handler(id) {
        if (id) {
          this.$store.dispatch("crmBoard/fetchBoard", { pipelineId: id });
        }
      },
    },
    // Watcher adicional para pipelines
    pipelines: {
      handler(newPipelines) {
        if (!this.selectedPipeline && newPipelines.length > 0) {
          this.selectedPipeline = newPipelines[0].id;
        }
      },
    },

    // Watcher para cargar conversaciones cuando cambia el contacto
    "dealForm.primary_contact_id"(newContactId, oldContactId) {
      if (newContactId !== oldContactId) {
        this.dealForm.conversation_id = null;
        this.loadContactConversations(newContactId).then(() => {
          const first = this.contactConversations.find((c) => c.id !== null);
          if (first) {
            this.dealForm.conversation_id = first.id;
          }
        });
      }
    },

    // Watcher para actualizar monto y moneda cuando cambian los productos
    "dealForm.product_ids": {
      handler(newProductIds) {
        // Comparar IDs actuales vs los cargados del backend.
        // Si coinciden, el watcher se disparó por la carga del form (no por el usuario)
        // y no debemos sobreescribir el monto guardado.
        const currentKey = (newProductIds || [])
          .map((p) => (typeof p === "object" ? p.id : p))
          .filter(Boolean)
          .sort((a, b) => a - b)
          .join(",");

        if (
          this._loadedProductIds !== null &&
          currentKey === this._loadedProductIds
        ) {
          return;
        }

        if (newProductIds && newProductIds.length > 0) {
          // Calcular monto automáticamente desde los productos seleccionados
          const totalCents = this.calculatedAmountFromProducts;
          this.dealForm.amount = (totalCents / 100).toFixed(2);

          // Actualizar moneda automáticamente (todos los productos tienen la misma moneda)
          const currency = this.selectedProductsCurrency;
          if (currency) {
            this.dealForm.currency = currency;
          }
        }
        // Si no hay productos, dejar monto y moneda editables (no cambiar valores)
      },
      deep: true,
    },
    // Watchers para limpiar errores de formulario de stage
    "stageForm.name"() {
      if (this.stageNameError && this.stageForm.name.trim()) {
        this.stageNameError = "";
      }
    },
    "stageForm.probability"() {
      if (
        this.stageProbabilityError &&
        this.stageForm.probability >= 0 &&
        this.stageForm.probability <= 100
      ) {
        this.stageProbabilityError = "";
      }
    },
    // Watchers para limpiar errores de formulario de deal
    "dealForm.title"() {
      if (this.dealTitleError && this.dealForm.title.trim()) {
        this.dealTitleError = "";
      }
    },
    "dealForm.amount"() {
      if (this.dealAmountError) {
        this.dealAmountError = "";
      }
    },
    // Watcher para limpiar error de formulario de pipeline
    "pipelineForm.name"() {
      if (this.pipelineNameError && this.pipelineForm.name.trim()) {
        this.pipelineNameError = "";
      }
    },
  },
  async mounted() {
    // Cargar pipelines al montar el componente
    await this.$store.dispatch("crmBoard/fetchPipelines");

    // Cargar equipos para el formulario
    await this.$store.dispatch("teams/get");

    // FORK: cargar etiquetas para el selector de deals.
    this.$store.dispatch("labels/get");

    // Si no hay pipeline seleccionado y hay pipelines disponibles, seleccionar el primero
    if (!this.selectedPipeline && this.pipelines.length > 0) {
      this.selectedPipeline = this.pipelines[0].id;
    }

    // FORK: si llegamos con ?deal_id= (deep-link desde la conversación), abrir el deal.
    await this.openDealFromRoute();

    // Agregar listener para cerrar menú al hacer clic fuera
    document.addEventListener("click", this.handleClickOutside);

    // Detección de viewport mobile para el board swipeable
    window.addEventListener("resize", this.handleWindowResize);

    // Reflejar en el buscador mobile cualquier query ya activo en el store
    this.mobileQuery = this.$store.state.crmBoard.filters?.query || "";
  },
  beforeUnmount() {
    // Limpiar listener
    document.removeEventListener("click", this.handleClickOutside);

    window.removeEventListener("resize", this.handleWindowResize);
    clearTimeout(this._resizeTimer);
    clearTimeout(this._mobileSearchTimer);
  },
  methods: {
    handleWindowResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this.isMobile = window.innerWidth < 768;
      }, 150);
    },
    // ===== Mobile: helpers de formato =====
    // Entradas de dinero por moneda, en unidades enteras (sin decimales).
    moneyEntries(byCurrency) {
      return Object.entries(byCurrency || {})
        .filter(([, cents]) => cents)
        .map(([currency, cents]) => ({
          currency,
          value: Math.round(cents / 100).toLocaleString("es-AR"),
        }));
    },
    formatDealAmount(deal) {
      if (!deal.amount_cents) return "—";
      const value = (deal.amount_cents / 100).toLocaleString("es-AR", {
        maximumFractionDigits: 0,
      });
      return `${deal.currency || ""} ${value}`.trim();
    },
    mobileOwnerName(deal) {
      return (
        deal.owner?.available_name || deal.owner?.name || "Sin responsable"
      );
    },
    // Antigüedad relativa a partir de created_at (Unix en segundos).
    formatDealAge(createdAt) {
      if (!createdAt) return "";
      const diff = Math.floor(Date.now() / 1000) - createdAt;
      if (diff < 3600) return `hace ${Math.max(1, Math.round(diff / 60))} min`;
      if (diff < 86400) return `hace ${Math.round(diff / 3600)} h`;
      return `hace ${Math.round(diff / 86400)} d`;
    },
    // ===== Mobile: selector de pipeline y menú de acciones =====
    selectMobilePipeline(id) {
      this.selectedPipeline = id;
      this.activeMobileStageIndex = 0;
      this.showMobilePipelineMenu = false;
    },
    selectMobileStage(idx) {
      this.activeMobileStageIndex = idx;
    },
    // ===== Mobile: acciones sobre la etapa activa =====
    openMobileStageSheet() {
      if (!this.activeStage) return;
      this.showMobileStageSheet = true;
    },
    editActiveStage() {
      this.showMobileStageSheet = false;
      this.handleEditStage(this.activeStage);
    },
    deleteActiveStage() {
      this.showMobileStageSheet = false;
      this.handleDeleteStage(this.activeStage);
    },
    // Reordenar recarga el board: hay que reubicar el índice activo por id,
    // si no el pill activo pasa a señalar la etapa vecina.
    async moveActiveStage(direction) {
      const stage = this.activeStage;
      if (!stage) return;
      this.showMobileStageSheet = false;
      if (direction === "left") {
        await this.handleMoveStageLeft(stage);
      } else {
        await this.handleMoveStageRight(stage);
      }
      const idx = this.stageOrder.findIndex(
        (id) => Number(id) === Number(stage.id),
      );
      if (idx !== -1) this.activeMobileStageIndex = idx;
    },
    // ===== Mobile: buscador y filtros =====
    onMobileSearchInput(e) {
      this.mobileQuery = e.target.value;
      clearTimeout(this._mobileSearchTimer);
      this._mobileSearchTimer = setTimeout(() => {
        this.$store.dispatch("crmBoard/applyFilters", {
          query: this.mobileQuery,
        });
      }, 300);
    },
    clearMobileFilterChip(key) {
      const payload = {};
      if (key === "query") {
        payload.query = "";
        this.mobileQuery = "";
      }
      if (key === "owner") payload.ownerIds = [];
      if (key === "date") {
        payload.createdAtFrom = null;
        payload.createdAtTo = null;
      }
      if (key === "amount") {
        payload.amountMin = null;
        payload.amountMax = null;
      }
      this.$store.dispatch("crmBoard/applyFilters", payload);
    },
    clearAllMobileFilters() {
      clearTimeout(this._mobileSearchTimer);
      this.mobileQuery = "";
      this.$store.dispatch("crmBoard/applyFilters", {
        query: "",
        ownerIds: [],
        createdAtFrom: null,
        createdAtTo: null,
        amountMin: null,
        amountMax: null,
      });
    },
    openMobileFilterSheet() {
      const f = this.$store.state.crmBoard.filters;
      this.mobileFilterDraft = {
        ownerIds: [...(f.ownerIds || [])],
        dateFrom: f.createdAtFrom
          ? formatUnixDate(f.createdAtFrom, "yyyy-MM-dd")
          : "",
        dateTo: f.createdAtTo ? formatUnixDate(f.createdAtTo, "yyyy-MM-dd") : "",
        amountMin: f.amountMin ?? null,
        amountMax: f.amountMax ?? null,
      };
      this.showMobileFilterSheet = true;
    },
    // El input type=date entrega "YYYY-MM-DD", que new Date() interpreta como UTC:
    // al oeste de UTC eso corre el filtro un día hacia atrás.
    parseDateInput(value) {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    toggleDraftOwner(id) {
      const idx = this.mobileFilterDraft.ownerIds.indexOf(id);
      if (idx === -1) this.mobileFilterDraft.ownerIds.push(id);
      else this.mobileFilterDraft.ownerIds.splice(idx, 1);
    },
    applyMobileFilters() {
      const d = this.mobileFilterDraft;
      const payload = {
        ownerIds: [...d.ownerIds],
        amountMin: d.amountMin !== "" && d.amountMin != null ? Number(d.amountMin) : null,
        amountMax: d.amountMax !== "" && d.amountMax != null ? Number(d.amountMax) : null,
      };
      // Rangos abiertos válidos: sólo desde, sólo hasta, o ambos.
      payload.createdAtFrom = d.dateFrom
        ? getUnixStartOfDay(this.parseDateInput(d.dateFrom))
        : null;
      payload.createdAtTo = d.dateTo
        ? getUnixEndOfDay(this.parseDateInput(d.dateTo))
        : null;
      this.$store.dispatch("crmBoard/applyFilters", payload);
      this.showMobileFilterSheet = false;
    },
    openNewPipelineForm() {
      this.showNewPipelineForm = true;
      // Resetear formulario
      this.pipelineForm = {
        name: "",
        deals_visibility: "team",
        team_ids: [],
      };
    },
    closeNewPipelineForm() {
      this.showNewPipelineForm = false;
      this.isEditingPipeline = false;
      this.pipelineForm = {
        name: "",
        deals_visibility: "team",
        team_ids: [],
      };
    },
    async createPipeline() {
      if (this.isEditingPipeline) {
        await this.updatePipeline();
        return;
      }

      // Limpiar error anterior
      this.pipelineNameError = "";

      // Validar campos
      const trimmedName = this.pipelineForm.name.trim();

      if (!trimmedName) {
        this.pipelineNameError = "El nombre del pipeline es obligatorio";
        return;
      }

      if (trimmedName.length < 3) {
        this.pipelineNameError = "El nombre debe tener al menos 3 caracteres";
        return;
      }

      if (trimmedName.length > 50) {
        this.pipelineNameError = "El nombre no puede exceder los 50 caracteres";
        return;
      }

      this.isCreatingPipeline = true;

      try {
        const response = await this.$store.dispatch("crmBoard/createPipeline", {
          name: trimmedName,
          deals_visibility: this.pipelineForm.deals_visibility,
          team_ids: this.pipelineForm.team_ids,
        });

        // Recargar pipelines
        await this.$store.dispatch("crmBoard/fetchPipelines");

        // Seleccionar el nuevo pipeline
        this.selectedPipeline = response.data.id;

        // Cerrar modal
        this.closeNewPipelineForm();

        // Mostrar mensaje de éxito
        useAlert("Pipeline creado exitosamente");
      } catch (error) {
        // Capturar mensaje específico del backend
        const errorMessage = error.response?.data?.message || "";

        // Manejar error de nombre duplicado
        if (
          errorMessage.toLowerCase().includes("already been taken") ||
          errorMessage.toLowerCase().includes("ha sido tomado") ||
          errorMessage.toLowerCase().includes("ya existe")
        ) {
          this.pipelineNameError = "Ya existe un pipeline con este nombre";
        } else {
          useAlert(
            errorMessage ||
              "Error al crear el pipeline. Por favor intenta de nuevo.",
          );
        }
      } finally {
        this.isCreatingPipeline = false;
      }
    },
    openNewStageForm() {
      this.stageForm = {
        name: "",
        probability: 0,
        position: null, // Se calculará automáticamente para nuevo stage
        stage_type: "regular",
        form_config: {
          enabled: false,
          fields: [],
        },
      };
      this.showNewStageForm = true;
    },
    closeNewStageForm() {
      this.showNewStageForm = false;
      this.isEditingStage = false;
      this.stageForm = {
        name: "",
        probability: 0,
        position: null,
      };
      // Limpiar errores
      this.stageNameError = "";
      this.stageProbabilityError = "";
    },
    async createStage() {
      // Limpiar errores anteriores
      this.stageNameError = "";
      this.stageProbabilityError = "";

      // Validar campos
      let hasError = false;

      if (!this.stageForm.name.trim()) {
        this.stageNameError = "El nombre de la etapa es requerido";
        hasError = true;
      } else if (this.stageForm.name.trim().length > 30) {
        this.stageNameError = "El nombre no puede exceder los 30 caracteres";
        hasError = true;
      }

      if (this.stageForm.probability < 0 || this.stageForm.probability > 100) {
        this.stageProbabilityError =
          "La probabilidad debe estar entre 0 y 100%";
        hasError = true;
      }

      if (hasError) return;

      if (!this.selectedPipeline) {
        useAlert("Debe seleccionar un pipeline");
        return;
      }

      this.isCreatingStage = true;

      try {
        const stageData = {
          name: this.stageForm.name.trim(),
          probability: parseInt(this.stageForm.probability),
          stage_type: this.stageForm.stage_type,
          form_config: this.stageForm.form_config,
        };

        const response = await CRMStagesAPI.create(
          this.selectedPipeline,
          stageData,
        );

        // Recargar el board para mostrar la nueva etapa
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        // Cerrar modal
        this.closeNewStageForm();

        // Mostrar mensaje de éxito
        useAlert("Etapa creada exitosamente");
      } catch (error) {
        console.error("Error creating stage:", error);
        useAlert("Error al crear la etapa. Por favor intenta de nuevo.");
      } finally {
        this.isCreatingStage = false;
      }
    },
    handleEditStage(stage) {
      this.isEditingStage = true;
      this.stageForm = {
        id: stage.id,
        name: stage.name,
        probability: stage.probability,
        position: stage.position,
        stage_type: stage.stage_type || "regular",
        form_config: stage.form_config || { enabled: false, fields: [] },
      };
      this.showNewStageForm = true;
    },
    async updateStage() {
      // Limpiar errores anteriores
      this.stageNameError = "";
      this.stageProbabilityError = "";

      // Validar campos
      let hasError = false;

      if (!this.stageForm.name.trim()) {
        this.stageNameError = "El nombre de la etapa es requerido";
        hasError = true;
      } else if (this.stageForm.name.trim().length > 30) {
        this.stageNameError = "El nombre no puede exceder los 30 caracteres";
        hasError = true;
      }

      if (this.stageForm.probability < 0 || this.stageForm.probability > 100) {
        this.stageProbabilityError =
          "La probabilidad debe estar entre 0 y 100%";
        hasError = true;
      }

      if (hasError) return;

      this.isCreatingStage = true;

      try {
        const stageData = {
          name: this.stageForm.name.trim(),
          probability: parseInt(this.stageForm.probability),
          position: this.stageForm.position, // Mantener la posición actual
          stage_type: this.stageForm.stage_type,
          form_config: this.stageForm.form_config,
        };

        await CRMStagesAPI.update(
          this.selectedPipeline,
          this.stageForm.id,
          stageData,
        );

        // Recargar el board para mostrar los cambios
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        // Cerrar modal
        this.closeNewStageForm();

        // Mostrar mensaje de éxito
        useAlert("Etapa actualizada exitosamente");
      } catch (error) {
        console.error("Error updating stage:", error);
        useAlert("Error al actualizar la etapa. Por favor intenta de nuevo.");
      } finally {
        this.isCreatingStage = false;
      }
    },
    handleDeleteStage(stage) {
      this.stageToDelete = stage;
      this.showDeleteStageModal = true;
    },
    async confirmDeleteStage() {
      if (!this.stageToDelete) {
        console.error("No stage to delete");
        return;
      }

      this.isDeletingStage = true;
      const stageName = this.stageToDelete.name; // Guardar el nombre antes de eliminar
      const stageId = this.stageToDelete.id;

      try {
        await CRMStagesAPI.delete(this.selectedPipeline, stageId);

        // Recargar el board
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        // Mostrar mensaje de éxito
        useAlert(`Etapa "${stageName}" eliminada exitosamente`);

        // Cerrar modal después del mensaje
        this.closeDeleteStageModal();
      } catch (error) {
        console.error("Error deleting stage:", error);
        useAlert("Error al eliminar la etapa. Por favor intenta de nuevo.");
      } finally {
        this.isDeletingStage = false;
      }
    },
    closeDeleteStageModal() {
      this.showDeleteStageModal = false;
      this.stageToDelete = null;
    },

    // Deal Methods
    async openNewDealForm() {
      this.dealForm = {
        title: "",
        pipeline_id: this.selectedPipeline,
        stage_id: null,
        owner_id: this.getCurrentUser?.id || null,
        primary_contact_id: null,
        conversation_id: null,
        amount: "",
        currency: "USD",
        expected_close_date: "",
        product_ids: [], // Array para soportar múltiples productos
        phone: "",
        contact_ids: [],
        payment_method_ids: [], // Array para soportar múltiples métodos de pago
        labels: [], // FORK
      }; // Cargar datos iniciales
      await this.loadStagesForPipeline(this.selectedPipeline);
      await this.loadAgentsForPipeline(this.selectedPipeline);
      await this.loadProducts();

      this.showNewDealForm = true;
    },

    closeNewDealForm() {
      this.showNewDealForm = false;
      this.resetDealForm();
    },

    async loadStagesForPipeline(pipelineId, preserveSelection = false) {
      if (!pipelineId) {
        this.availableStages = [];
        return;
      }

      try {
        const response = await CRMStagesAPI.get(pipelineId);
        this.availableStages = response.data || [];

        // Solo seleccionar la primera etapa por defecto si no estamos preservando la selección
        if (!preserveSelection && this.availableStages.length > 0) {
          this.dealForm.stage_id = this.availableStages[0].id;
        }
      } catch (error) {
        console.error("Error loading stages:", error);
        this.availableStages = [];
      }
    },

    async loadAgentsForPipeline(pipelineId) {
      if (!pipelineId) {
        this.availableAgents = [];
        return;
      }

      try {
        // Obtener el pipeline para conseguir los teams
        const pipelineResponse = await CRMPipelinesAPI.show(pipelineId);
        const pipeline = pipelineResponse.data;

        if (pipeline.team_ids && pipeline.team_ids.length > 0) {
          // Obtener agentes de todos los teams del pipeline
          const agentsPromises = pipeline.team_ids.map((teamId) =>
            TeamsAPI.getAgents({ teamId }),
          );

          const agentsResponses = await Promise.all(agentsPromises);

          // Combinar y deduplicar agentes
          const allAgents = [];
          const agentIds = new Set();

          agentsResponses.forEach((response) => {
            const agents = response.data || [];
            agents.forEach((agent) => {
              if (!agentIds.has(agent.id)) {
                agentIds.add(agent.id);
                // Transformar formato para SearchableSelect {id, name}
                allAgents.push({
                  id: agent.id,
                  name:
                    agent.available_name ||
                    agent.name ||
                    agent.email ||
                    `Agent ${agent.id}`,
                });
              }
            });
          });

          this.availableAgents = allAgents;
        } else {
          this.availableAgents = [];
        }
      } catch (error) {
        console.error("[CRM] Error loading agents:", error);
        this.availableAgents = [];
      }
    },

    async loadProducts() {
      try {
        const response = await CRMProductsAPI.get({ active: true });
        this.availableProducts = response.data || [];
      } catch (error) {
        this.availableProducts = [];
      }
    },

    async loadContactConversations(contactId) {
      if (!contactId) {
        this.contactConversations = [];
        return;
      }
      this.isLoadingConversations = true;
      try {
        const response = await ContactAPI.getConversations(contactId);
        const conversations = response.data?.payload || [];
        const items = conversations.map((c) => ({
          id: c.id,
          name: `#${c.id}`,
        }));
        this.contactConversations = [
          { id: null, name: "Sin conversación" },
          ...items,
        ];
      } catch (error) {
        this.contactConversations = [];
      } finally {
        this.isLoadingConversations = false;
      }
    },

    async onPipelineChange() {
      if (this.dealForm.pipeline_id) {
        // Si estamos editando un deal, preservamos las selecciones actuales
        const isEditing = this.showEditDealForm && this.editingDeal;
        await this.loadStagesForPipeline(this.dealForm.pipeline_id, isEditing);
        await this.loadAgentsForPipeline(this.dealForm.pipeline_id);
      }
    },

    async createDeal() {
      // Limpiar errores anteriores
      this.dealTitleError = "";
      this.dealAmountError = "";

      // Validar campos
      let hasError = false;
      const trimmedTitle = this.dealForm.title.trim();

      if (!trimmedTitle) {
        this.dealTitleError = "El nombre del negocio es requerido";
        hasError = true;
      } else if (trimmedTitle.length > 100) {
        this.dealTitleError = "El nombre no puede exceder los 100 caracteres";
        hasError = true;
      }

      if (!this.dealForm.pipeline_id) {
        useAlert("Debe seleccionar un pipeline");
        hasError = true;
      }

      if (!this.dealForm.stage_id) {
        useAlert("Debe seleccionar una etapa");
        hasError = true;
      }

      if (hasError) return;

      this.isCreatingDeal = true;

      try {
        // Convertir amount a centavos
        const amountCents = this.dealForm.amount
          ? Math.round(parseFloat(this.dealForm.amount) * 100)
          : 0;

        const dealData = {
          title: trimmedTitle,
          pipeline_id: this.dealForm.pipeline_id,
          stage_id: this.dealForm.stage_id,
          owner_id: this.dealForm.owner_id || null,
          primary_contact_id: this.dealForm.primary_contact_id || null,
          amount_cents: amountCents,
          currency: this.dealForm.currency,
          expected_close_date: this.dealForm.expected_close_date || null,
          // Campos directos en lugar de custom_attributes
          phone: this.dealForm.phone || null,
          conversation_id: this.dealForm.conversation_id || null,
          // Extraer solo IDs de los objetos MultiSelect
          product_ids:
            this.dealForm.product_ids?.map((item) =>
              typeof item === "object" ? item.id : item,
            ) || [],
          payment_method_ids:
            this.dealForm.payment_method_ids?.map((item) =>
              typeof item === "object" ? item.id : item,
            ) || [],
          // FORK: labels como array de títulos
          labels:
            this.dealForm.labels?.map((item) =>
              typeof item === "object" ? item.id : item,
            ) || [],
        };

        await CRMDealsAPI.create(dealData);

        // Recargar el board
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        // Cerrar modal
        this.closeNewDealForm();

        // Mostrar mensaje de éxito
        useAlert("Negocio creado exitosamente");
      } catch (error) {
        console.error("Error creating deal:", error);
        useAlert("Error al crear el negocio. Por favor intenta de nuevo.");
      } finally {
        this.isCreatingDeal = false;
      }
    },

    // ===== MÉTODOS DE EDICIÓN DE DEALS =====
    // FORK: deep-link a un deal puntual (?deal_id=). Trae el deal por id y abre el modal.
    // No depende de que el deal esté en el board (puede estar en página 2+ de un stage):
    // lo guarda en routeDeal, que editingDeal usa como fallback sobre dealsById.
    async openDealFromRoute() {
      const dealId = parseInt(this.$route.query.deal_id, 10);
      if (!dealId) return;

      try {
        const { data: deal } = await CRMDealsAPI.show(dealId);
        // Mostrar el pipeline del deal detrás del modal (dispara fetchBoard vía watcher).
        if (deal.pipeline_id && this.selectedPipeline !== deal.pipeline_id) {
          this.selectedPipeline = deal.pipeline_id;
        }
        this.routeDeal = deal;
        await this.openEditDealForm(deal);
      } catch (error) {
        useAlert("No se pudo abrir el negocio solicitado.");
      }
    },
    async openEditDealForm(deal) {
      this.editingDealId = deal.id;

      // Primero cargar todos los datos necesarios
      try {
        // 1. Cargar stages y agents para el pipeline del deal ANTES de llenar el formulario
        if (deal.pipeline_id || deal.pipeline?.id) {
          const pipelineId = deal.pipeline_id || deal.pipeline?.id;

          await Promise.all([
            this.loadStagesForPipeline(pipelineId, true), // preserveSelection = true
            this.loadAgentsForPipeline(pipelineId),
            this.loadProducts(),
          ]);
        }

        // 3. Guardar los IDs de productos del backend ANTES de asignar el form.
        // El watcher compara los IDs actuales con estos para saber si el cambio vino
        // del usuario o de la carga inicial, evitando sobreescribir el monto guardado.
        this._loadedProductIds = (deal.products || [])
          .map((p) => p.id)
          .filter(Boolean)
          .sort((a, b) => a - b)
          .join(",");

        this.dealForm = {
          title: deal.title || "",
          pipeline_id: deal.pipeline_id || deal.pipeline?.id || null,
          stage_id: deal.stage_id || deal.stage?.id || null,
          owner_id: deal.owner_id || deal.owner?.id || null,
          primary_contact_id:
            deal.primary_contact_id || deal.primary_contact?.id || null,
          conversation_id: deal.conversation_id || null,
          amount: deal.amount_cents ? (deal.amount_cents / 100).toString() : "",
          currency: deal.currency || "USD",
          expected_close_date: deal.expected_close_date || "",
          product_ids: deal.products
            ? deal.products.map((p) => ({ id: p.id, name: p.name }))
            : deal.product_id
              ? [{ id: deal.product_id, name: "Producto" }]
              : [],
          phone: deal.phone || "",
          contact_ids: [],
          payment_method_ids: deal.payment_method_ids
            ? deal.payment_method_ids.map((id) => {
                const method = this.paymentMethodOptions.find(
                  (m) => m.id === id,
                );
                return method || { id, name: `Método ${id}` };
              })
            : [],
          // FORK: labels llegan como array de títulos; mapear a {id,name} para el multiselect.
          labels: (deal.labels || []).map((title) => ({
            id: title,
            name: title,
          })),
        };

        // 4. Verificar que las selecciones existan en las opciones disponibles
        if (
          this.dealForm.stage_id &&
          !this.availableStages.find((s) => s.id === this.dealForm.stage_id)
        ) {
          this.dealForm.stage_id =
            this.availableStages.length > 0 ? this.availableStages[0].id : null;
        }

        if (
          this.dealForm.owner_id &&
          !this.availableAgents.find((a) => a.id === this.dealForm.owner_id)
        ) {
          this.dealForm.owner_id = null;
        }

        // 5. Cargar conversaciones del contacto (sin auto-seleccionar, el deal ya tiene su conversation_id)
        if (this.dealForm.primary_contact_id) {
          this.loadContactConversations(this.dealForm.primary_contact_id);
        }

        this.showEditDealForm = true;
      } catch (error) {
        console.error("Error loading edit deal data:", error);
        useAlert(
          "Error al cargar los datos para editar el negocio. Por favor intenta de nuevo.",
        );
      }
    },

    closeEditDealForm() {
      this.showEditDealForm = false;
      this.showDeleteConfirmation = false;
      this.editingDealId = null;
      this.routeDeal = null; // FORK: limpiar el deal del deep-link
      this._loadedProductIds = null; // Limpiar IDs cargados al cerrar el form de edición
      this.resetDealForm();
    },

    // ===== MÉTODOS DE BORRADO DE DEALS =====
    showDeleteDealConfirmation() {
      this.showDeleteConfirmation = true;
    },

    hideDeleteDealConfirmation() {
      this.showDeleteConfirmation = false;
    },

    async deleteDeal() {
      if (!this.editingDeal) {
        alert("No hay negocio seleccionado para eliminar");
        return;
      }

      // Guardar el título antes de eliminar
      const dealTitle = this.editingDeal.title;

      this.isDeletingDeal = true;

      try {
        await this.$store.dispatch("crmBoard/deleteDeal", {
          dealId: this.editingDeal.id,
        });

        // Cerrar modal
        this.closeEditDealForm();

        // Mostrar mensaje de éxito usando el título guardado
        useAlert(`Negocio "${dealTitle}" eliminado exitosamente`);
      } catch (error) {
        useAlert("Error al eliminar el negocio. Por favor intenta de nuevo.");
      } finally {
        this.isDeletingDeal = false;
      }
    },

    resetDealForm() {
      this.dealForm = {
        title: "",
        pipeline_id: null,
        stage_id: null,
        owner_id: null,
        primary_contact_id: null,
        conversation_id: null,
        amount: "",
        currency: "USD",
        expected_close_date: "",
        product_ids: [], // Array para soportar múltiples productos
        phone: "",
        contact_ids: [],
        payment_method_ids: [], // Array para soportar múltiples métodos de pago
        labels: [], // FORK
      };
      this.contactConversations = [];
      this.availableStages = [];
      this.availableAgents = [];
      // Limpiar errores
      this.dealTitleError = "";
      this.dealAmountError = "";
    },

    async updateDeal() {
      // Limpiar errores anteriores
      this.dealTitleError = "";

      const trimmedTitle = this.dealForm.title.trim();

      if (!trimmedTitle) {
        this.dealTitleError = "El nombre del negocio es requerido";
        return;
      }

      if (trimmedTitle.length > 100) {
        this.dealTitleError = "El nombre no puede exceder los 100 caracteres";
        return;
      }

      if (!this.dealForm.pipeline_id) {
        useAlert("Debe seleccionar un pipeline");
        return;
      }

      if (!this.dealForm.stage_id) {
        useAlert("Debe seleccionar una etapa");
        return;
      }

      this.isUpdatingDeal = true;

      // Capturar la etapa original ANTES de guardar: el update no envía
      // stage_id, así que editingDeal.stage_id sigue siendo la original y sirve
      // para detectar si el usuario cambió la etapa en el <select>.
      const dealId = this.editingDeal.id;
      const originalStageId = Number(this.editingDeal.stage_id);
      const targetStageId = Number(this.dealForm.stage_id);
      const stageChanged =
        !!targetStageId && targetStageId !== originalStageId;

      try {
        // Convertir amount a centavos
        const amountCents = this.dealForm.amount
          ? Math.round(parseFloat(this.dealForm.amount) * 100)
          : 0;

        // stage_id NO viaja acá: el cambio de etapa se procesa aparte (abajo)
        // vía DealStageMover, para pasar por MoveStageService (formulario del
        // stage, custom_attributes, efectos de status). Mandarlo por este
        // endpoint plano bypassearía todo ese flujo.
        const dealData = {
          title: trimmedTitle,
          pipeline_id: this.dealForm.pipeline_id,
          owner_id: this.dealForm.owner_id || null,
          primary_contact_id: this.dealForm.primary_contact_id || null,
          amount_cents: amountCents,
          currency: this.dealForm.currency,
          expected_close_date: this.dealForm.expected_close_date || null,
          phone: this.dealForm.phone || null,
          conversation_id: this.dealForm.conversation_id || null,
          product_ids:
            this.dealForm.product_ids?.map((item) =>
              typeof item === "object" ? item.id : item,
            ) || [],
          payment_method_ids:
            this.dealForm.payment_method_ids?.map((item) =>
              typeof item === "object" ? item.id : item,
            ) || [],
          // FORK: labels como array de títulos
          labels:
            this.dealForm.labels?.map((item) =>
              typeof item === "object" ? item.id : item,
            ) || [],
        };

        // 1. Guardar los campos no-etapa.
        await this.$store.dispatch("crmBoard/updateDeal", { dealId, dealData });

        // 2. Si cambió la etapa, procesar el movimiento al guardar (no antes).
        //    Si el stage destino pide formulario, DealMoveForm aparece ahora
        //    por encima; el mover vive en la raíz de la vista, así que sigue
        //    visible aunque el modal se cierre.
        if (stageChanged) {
          await this.$refs.dealStageMover.startMove({
            deal: { id: dealId, stage_id: originalStageId },
            targetStageId,
          });
        }

        // Cerrar modal
        this.closeEditDealForm();

        // Mostrar mensaje de éxito
        useAlert("Negocio actualizado exitosamente");
      } catch (error) {
        console.error("Error updating deal:", error);
        useAlert("Error al actualizar el negocio. Por favor intenta de nuevo.");
      } finally {
        this.isUpdatingDeal = false;
      }
    },

    // ===== MÉTODOS DE ACTIVIDADES =====
    async openCreateActivityModal(data) {
      this.selectedDealForActivities = data.deal;
      this.activityForm = {
        activity_type: data.activityType,
        subject: "",
        description: "",
        due_at: "",
        owner_id: this.getCurrentUser.id,
        deal_id: data.deal.id,
      };
      this.showCreateActivityModal = true;
    },

    closeCreateActivityModal() {
      this.showCreateActivityModal = false;
      this.resetActivityForm();
    },

    async createActivity() {
      if (!this.activityForm.subject.trim()) {
        useAlert("El asunto de la actividad es requerido");
        return;
      }

      this.isCreatingActivity = true;

      try {
        await CRMActivitiesAPI.create(this.activityForm);

        // Recargar el board para actualizar el contador de actividades
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        this.closeCreateActivityModal();

        useAlert("Actividad creada exitosamente");
      } catch (error) {
        console.error("Error creating activity:", error);
        useAlert("Error al crear la actividad. Por favor intenta de nuevo.");
      } finally {
        this.isCreatingActivity = false;
      }
    },

    resetActivityForm() {
      this.activityForm = {
        activity_type: "task",
        subject: "",
        description: "",
        due_at: "",
        owner_id: this.getCurrentUser.id,
        deal_id: null,
      };
    },

    async completeActivity({ activityId, dealId }) {
      try {
        // Completar la actividad usando la API
        await CRMActivitiesAPI.update(activityId, {
          completed_at: new Date().toISOString(),
        });

        // Recargar el board para actualizar el estado de las actividades
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        useAlert("Actividad completada exitosamente");
      } catch (error) {
        console.error("Error completing activity:", error);
        useAlert("Error al completar la actividad");
      }
    },

    async deleteActivity({ activityId, dealId }) {
      try {
        // Eliminar la actividad usando la API
        await CRMActivitiesAPI.delete(activityId);

        // Recargar el board para actualizar la lista de actividades
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        useAlert("Actividad eliminada exitosamente");
      } catch (error) {
        console.error("Error deleting activity:", error);
        useAlert("Error al eliminar la actividad");
      }
    },

    // ===== MÉTODOS DE MODAL DE DETALLES DE ACTIVIDAD =====
    openActivityDetailModal(data) {
      this.selectedActivity = data.activity;
      this.selectedDealForActivityDetail = data.deal;
      this.showActivityDetailModal = true;
    },

    closeActivityDetailModal() {
      this.showActivityDetailModal = false;
      this.selectedActivity = null;
      this.selectedDealForActivityDetail = null;
    },

    async completeActivityFromModal() {
      if (!this.selectedActivity?.id) return;

      try {
        await CRMActivitiesAPI.update(this.selectedActivity.id, {
          completed_at: new Date().toISOString(),
        });

        // Recargar el board para actualizar el estado
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        this.closeActivityDetailModal();

        useAlert("Actividad completada exitosamente");
      } catch (error) {
        console.error("Error completing activity:", error);
        useAlert("Error al completar la actividad");
      }
    },

    async deleteActivityFromModal() {
      if (!this.selectedActivity?.id) return;

      // Confirmación antes de eliminar
      if (!confirm("¿Estás seguro de que quieres eliminar esta actividad?")) {
        return;
      }

      try {
        await CRMActivitiesAPI.delete(this.selectedActivity.id);

        // Recargar el board para actualizar la lista
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        this.closeActivityDetailModal();

        useAlert("Actividad eliminada exitosamente");
      } catch (error) {
        console.error("Error deleting activity:", error);
        useAlert("Error al eliminar la actividad");
      }
    },

    getActivityTypeIcon(type) {
      const icons = {
        task: "📋",
        call: "📞",
        meeting: "🤝",
        email: "📧",
        note: "📝",
      };
      return icons[type] || "📋";
    },

    getActivityTypeInSpanish(type) {
      const types = {
        task: "Tarea",
        call: "Llamada",
        meeting: "Reunión",
        email: "Email",
        note: "Nota",
      };
      return types[type] || type;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getActivityTypeName(type) {
      const names = {
        task: "Tarea",
        call: "Llamada",
        meeting: "Reunión",
        email: "Email",
        note: "Nota",
      };
      return names[type] || "Tarea";
    },
    async handleMoveStageLeft(stage) {
      try {
        await CRMStagesAPI.reorder(this.selectedPipeline, "left", stage.id);

        // Recargar el board para mostrar los cambios
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        // Mostrar mensaje de éxito
        useAlert("Etapa movida hacia la izquierda");
      } catch (error) {
        console.error("Error moving stage left:", error);
        useAlert(
          "Error al mover la etapa hacia la izquierda. Por favor intenta de nuevo.",
        );
      }
    },
    async handleMoveStageRight(stage) {
      try {
        await CRMStagesAPI.reorder(this.selectedPipeline, "right", stage.id);

        // Recargar el board para mostrar los cambios
        await this.$store.dispatch("crmBoard/fetchBoard", {
          pipelineId: this.selectedPipeline,
        });

        // Mostrar mensaje de éxito
        useAlert("Etapa movida hacia la derecha");
      } catch (error) {
        console.error("Error moving stage right:", error);
        useAlert(
          "Error al mover la etapa hacia la derecha. Por favor intenta de nuevo.",
        );
      }
    },
    toggleTeamSelection(teamId) {
      const index = this.pipelineForm.team_ids.indexOf(teamId);
      if (index === -1) {
        // Agregar equipo
        this.pipelineForm.team_ids.push(teamId);
      } else {
        // Remover equipo
        this.pipelineForm.team_ids.splice(index, 1);
      }
    },
    isTeamSelected(teamId) {
      return this.pipelineForm.team_ids.includes(teamId);
    },
    getSelectedTeams() {
      return this.getTeams.filter((team) => this.isTeamSelected(team.id));
    },
    togglePipelineMenu() {
      this.showPipelineMenu = !this.showPipelineMenu;
    },
    closePipelineMenu() {
      this.showPipelineMenu = false;
    },
    editPipeline() {
      if (!this.currentPipeline) return;

      // Cargar datos del pipeline actual en el formulario
      this.pipelineForm = {
        name: this.currentPipeline.name,
        deals_visibility: this.currentPipeline.deals_visibility,
        team_ids: [...(this.currentPipeline.team_ids || [])],
      };

      this.isEditingPipeline = true;
      this.showNewPipelineForm = true;
      this.closePipelineMenu();
    },
    async updatePipeline() {
      // Limpiar error anterior
      this.pipelineNameError = "";

      // Validar campos
      const trimmedName = this.pipelineForm.name.trim();

      if (!trimmedName) {
        this.pipelineNameError = "El nombre del pipeline es obligatorio";
        return;
      }

      if (trimmedName.length < 3) {
        this.pipelineNameError = "El nombre debe tener al menos 3 caracteres";
        return;
      }

      if (trimmedName.length > 50) {
        this.pipelineNameError = "El nombre no puede exceder los 50 caracteres";
        return;
      }

      this.isCreatingPipeline = true;

      try {
        await this.$store.dispatch("crmBoard/updatePipeline", {
          id: this.currentPipeline.id,
          name: trimmedName,
          deals_visibility: this.pipelineForm.deals_visibility,
          team_ids: this.pipelineForm.team_ids,
        });

        // Recargar pipelines
        await this.$store.dispatch("crmBoard/fetchPipelines");

        // Cerrar modal
        this.closeNewPipelineForm();

        // Mostrar mensaje de éxito
        useAlert("Pipeline actualizado exitosamente");
      } catch (error) {
        // Capturar mensaje específico del backend
        const errorMessage = error.response?.data?.message || "";

        // Manejar error de nombre duplicado
        if (
          errorMessage.toLowerCase().includes("already been taken") ||
          errorMessage.toLowerCase().includes("ha sido tomado") ||
          errorMessage.toLowerCase().includes("ya existe")
        ) {
          this.pipelineNameError = "Ya existe un pipeline con este nombre";
        } else {
          useAlert(
            errorMessage ||
              "Error al actualizar el pipeline. Por favor intenta de nuevo.",
          );
        }
      } finally {
        this.isCreatingPipeline = false;
      }
    },
    async deletePipeline() {
      if (!this.currentPipeline) return;

      // Mostrar modal de confirmación y cargar estadísticas
      this.showDeleteConfirmationModal = true;
      this.deleteConfirmation.confirmationText = "";
      this.deleteConfirmation.isLoadingStats = true;
      this.closePipelineMenu();

      try {
        // Usar el API importado estáticamente
        const response = await CRMPipelinesAPI.getDeletionStats(
          this.currentPipeline.id,
        );
        this.deleteConfirmation.stats = response.data;
      } catch (error) {
        console.error("Error loading deletion stats:", error);
        useAlert("Error al cargar información del pipeline");
        this.closeDeleteConfirmationModal();
      } finally {
        this.deleteConfirmation.isLoadingStats = false;
      }
    },
    closeDeleteConfirmationModal() {
      this.showDeleteConfirmationModal = false;
      this.deleteConfirmation.stats = null;
      this.deleteConfirmation.confirmationText = "";
    },
    async confirmDeletePipeline() {
      if (!this.currentPipeline || !this.deleteConfirmation.stats) return;

      // Verificar que se escribió correctamente el nombre del pipeline
      if (
        this.deleteConfirmation.confirmationText !== this.currentPipeline.name
      ) {
        alert(
          `Debes escribir exactamente "${this.currentPipeline.name}" para confirmar`,
        );
        return;
      }

      // Confirmación final
      if (
        !confirm(
          `¿Estás absolutamente seguro? Esta acción eliminará permanentemente el pipeline "${this.currentPipeline.name}" y todo su contenido. Esta acción NO se puede deshacer.`,
        )
      ) {
        return;
      }

      this.isDeletingPipeline = true;

      try {
        await this.$store.dispatch(
          "crmBoard/deletePipeline",
          this.currentPipeline.id,
        );

        // Recargar pipelines
        await this.$store.dispatch("crmBoard/fetchPipelines");

        // Si se eliminó el pipeline actual, seleccionar el primero disponible
        if (this.pipelines.length > 0) {
          this.selectedPipeline = this.pipelines[0].id;
        } else {
          this.selectedPipeline = null;
        }

        this.closeDeleteConfirmationModal();

        // Mostrar mensaje de éxito
        useAlert("Pipeline eliminado exitosamente");
      } catch (error) {
        console.error("Error deleting pipeline:", error);
        useAlert("Error al eliminar el pipeline. Por favor intenta de nuevo.");
      } finally {
        this.isDeletingPipeline = false;
      }
    },

    // ===== CAMBIO DE ETAPA (compartido entre drag y modal de edición) =====
    // Punto de entrada desde el drag del board (StageColumn @request-move).
    handleRequestMove({ dealId, targetStageId }) {
      const deal = this.$store.state.crmBoard.dealsById[dealId];
      if (!deal) return;
      this.$refs.dealStageMover.startMove({ deal, targetStageId });
    },

    // DealStageMover consultó al server y el stage destino requiere formulario.
    handleDealMoveFormRequired({ deal, targetStage }) {
      this.pendingMoveShowedForm = true;

      // Solo el drag necesita rollback visual: vuedraggable ya puso la card en
      // el stage destino (vía SET_STAGE_DEALS) antes de saber si requería
      // formulario, así que la devolvemos al origen mientras se completa. Un
      // movimiento originado en el modal de edición no tiene card que revertir.
      const isEditModalMove =
        this.showEditDealForm && Number(this.editingDealId) === Number(deal.id);
      if (!isEditModalMove) {
        this.$store.commit("crmBoard/MOVE_DEAL_LOCAL", {
          dealId: deal.id,
          fromStageId: targetStage.id,
          toStageId: deal.stage_id,
        });
      }
    },

    // El movimiento se completó (con o sin formulario de por medio).
    handleDealMoved(deal) {
      const showedForm = this.pendingMoveShowedForm;
      this.pendingMoveShowedForm = false;

      this.$store.commit("crmBoard/UPDATE_DEAL_LOCAL", deal);

      // Re-sync dealForm si el panel de edición está abierto para el mismo deal:
      // el formulario del stage pudo haber actualizado monto/productos también.
      if (
        this.showEditDealForm &&
        Number(this.editingDealId) === Number(deal.id)
      ) {
        this.dealForm.stage_id = deal.stage_id;
        this.dealForm.amount = deal.amount_cents
          ? (deal.amount_cents / 100).toString()
          : "";
        this.dealForm.currency = deal.currency || "USD";
        this.dealForm.product_ids = deal.products
          ? deal.products.map((p) => ({ id: p.id, name: p.name }))
          : [];
      }

      // Igual que el drag: silencioso cuando no hubo formulario de por medio
      // (evita ruido en cada arrastre); solo alerta cuando hubo un paso
      // explícito del usuario para completar el movimiento.
      if (showedForm) {
        useAlert("Deal movido exitosamente");
      }
    },

    // La llamada inicial (sin form_data) falló antes de que el servidor
    // confirmara si requería formulario — p.ej. error de red. Mismo revert
    // que handleDealMoveFormRequired, usando el targetStageId del intento.
    handleDealMoveFailed({ deal, targetStageId }) {
      this.pendingMoveShowedForm = false;
      const isEditModalMove =
        this.showEditDealForm && Number(this.editingDealId) === Number(deal.id);
      if (!isEditModalMove) {
        this.$store.commit("crmBoard/MOVE_DEAL_LOCAL", {
          dealId: deal.id,
          fromStageId: targetStageId,
          toStageId: deal.stage_id,
        });
      }
    },

    handleDealMoveCancelled() {
      this.pendingMoveShowedForm = false;
      // El rollback visual del drag (si aplicaba) ya fue aplicado en
      // handleDealMoveFormRequired. En el modal de edición el cambio se
      // dispara al guardar y el modal ya se cerró, así que no hay nada que
      // revertir: la etapa simplemente no cambió.
    },

    handleClickOutside(event) {
      // Cerrar menú si se hace clic fuera del menú dropdown
      if (!event.target.closest(".relative")) {
        this.closePipelineMenu();
      }
    },
  },
};
</script>

<template>
  <div
    class="w-full max-w-none flex-1 min-w-0 h-full bg-n-background overflow-hidden flex flex-col"
  >
    <!-- ================= DESKTOP ================= -->
    <template v-if="!isMobile">
    <!-- Header con selector de pipeline y botón nuevo pipeline -->
    <div
      class="flex-shrink-0 px-6 py-4 bg-white dark:bg-n-solid-3 border-b border-n-weak"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4 min-w-0 flex-1">
          <div class="flex items-center gap-2 min-w-0 flex-1">
            <!-- Selector de Pipeline -->
            <div class="w-64">
              <Select
                v-model="selectedPipeline"
                :options="pipelines"
                placeholder="Seleccionar pipeline"
              />
            </div>

            <!-- Menú de tres puntos para pipeline -->
            <div v-if="shouldShowPipelineMenu" class="relative">
              <button
                class="p-2 hover:bg-n-slate-9/10 rounded-lg transition-all cursor-pointer border-0 outline-none"
                :class="{ 'bg-n-slate-9/10': showPipelineMenu }"
                @click="togglePipelineMenu"
              >
                <svg
                  class="w-5 h-5 text-n-slate-11"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                  />
                </svg>
              </button>

              <!-- Dropdown menú -->
              <div
                v-if="showPipelineMenu"
                class="absolute top-full right-0 mt-1 w-48 bg-white dark:bg-n-solid-3 rounded-lg shadow-lg border border-n-weak z-50"
                @click.stop
              >
                <div class="py-1">
                  <button
                    class="w-full flex items-center gap-2 px-4 py-2 text-sm text-n-slate-12 hover:bg-n-slate-9/5 transition-all cursor-pointer border-0 outline-none"
                    @click="editPipeline"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Modificar Pipeline
                  </button>

                  <button
                    :disabled="isDeletingPipeline"
                    class="w-full flex items-center gap-2 px-4 py-2 text-sm text-n-ruby-10 hover:bg-n-ruby-10/5 transition-all cursor-pointer border-0 outline-none disabled:opacity-50"
                    @click="deletePipeline"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    {{
                      isDeletingPipeline ? "Eliminando..." : "Borrar Pipeline"
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Botón nuevo pipeline -->
          <button
            v-if="isAdmin"
            class="px-4 py-2 text-sm bg-n-brand hover:brightness-110 text-white rounded-lg font-medium transition-all cursor-pointer border-0 outline-none whitespace-nowrap min-w-fit flex-shrink-0"
            @click="openNewPipelineForm"
          >
            + Nuevo Pipeline
          </button>
        </div>
      </div>
    </div>

    <div
      class="border-t border-n-weak flex items-center justify-between py-4 px-6 bg-white dark:bg-n-solid-3"
    >
      <div class="flex flex-col min-w-0">
        <h2 class="font-semibold truncate text-xl">
          {{ pipeline?.name }}
        </h2>
        <div
          class="flex gap-4 text-sm text-slate-600 dark:text-slate-300 mt-1"
        >
          <span class="whitespace-nowrap">Total leads: {{ totals.uniqueLeadCount }}</span>
          <span class="whitespace-nowrap">Total deals: {{ totals.dealCount }}</span>
          <span class="whitespace-nowrap">
            Total $:
            <template v-if="Object.keys(totals.sumByCurrency || {}).length">
              <span
                v-for="(amount, currency, idx) in totals.sumByCurrency"
                :key="currency"
              >
                {{
                  (amount / 100).toLocaleString(undefined, {
                    style: "currency",
                    currency,
                  })
                }}
                <span v-if="idx < Object.keys(totals.sumByCurrency).length - 1">
                  ·
                </span>
              </span>
            </template>
            <template v-else>—</template>
          </span>
          <span class="whitespace-nowrap">
            Total $ ponderado:
            <template
              v-if="Object.keys(totals.weightedSumByCurrency || {}).length"
            >
              <span
                v-for="(amount, currency, idx) in totals.weightedSumByCurrency"
                :key="currency"
              >
                {{
                  (amount / 100).toLocaleString(undefined, {
                    style: "currency",
                    currency,
                  })
                }}
                <span
                  v-if="
                    idx < Object.keys(totals.weightedSumByCurrency).length - 1
                  "
                >
                  ·
                </span>
              </span>
            </template>
            <template v-else>—</template>
          </span>
        </div>
      </div>
      <div class="flex items-center gap-4">
        <!-- Botones Nueva Etapa y Nuevo Negocio debajo del header -->
        <div
          v-if="selectedPipeline"
          class="flex mt-4 justify-end space-x-3"
        >
          <button
            class="px-4 py-2 text-sm bg-n-brand/10 hover:bg-n-brand/20 text-n-blue-text rounded-lg font-medium transition-all cursor-pointer border-0 outline-none whitespace-nowrap"
            @click="openNewDealForm"
          >
            + Nuevo Negocio
          </button>

          <button
            v-if="isAdmin"
            class="px-4 py-2 text-sm bg-n-brand/10 hover:bg-n-brand/20 text-n-blue-text rounded-lg font-medium transition-all cursor-pointer border-0 outline-none whitespace-nowrap"
            @click="openNewStageForm"
          >
            + Nueva Etapa
          </button>
        </div>
      </div>
    </div>

    <!-- Área de scroll principal -->
    <div class="flex-1 overflow-y-auto">
      <!-- Card principal del tablero con altura completa -->
      <div
        class="bg-white dark:bg-n-solid-3 shadow-lg border-n-weak overflow-hidden h-full flex flex-col min-h-[calc(100vh-200px)]"
      >
        <!-- Área de columnas con scroll horizontal y altura que se expande -->
        <div class="flex-1 min-h-0 bg-slate-50 dark:bg-slate-800 flex flex-col">
          <!-- Barra de filtros de deals -->
          <DealFilters :key="selectedPipeline" />

          <!-- Contenido cuando hay stages -->
          <div class="flex-1 min-h-0 relative p-6">
            <!-- Overlay global de carga del board (carga inicial, cambio de pipeline, crear/eliminar stage) -->
            <div
              v-if="isBoardLoading"
              class="absolute inset-0 z-20 flex items-center justify-center bg-slate-200/90 dark:bg-slate-900/90 rounded-lg"
            >
              <div class="flex flex-col items-center gap-3">
                <div
                  class="w-8 h-8 border-[3px] border-n-brand border-t-transparent rounded-full animate-spin"
                />
                <p class="text-sm text-n-slate-11 font-medium">
                  Cargando tablero...
                </p>
              </div>
            </div>

            <div
              v-if="stageOrder.length > 0"
              class="flex h-full space-x-6 overflow-x-auto"
              style="scrollbar-width: thin"
            >
              <StageColumn
                v-for="stageId in stageOrder"
                :key="stageId"
                :stage="stagesById[stageId]"
                :total-stages="stageOrder.length"
                class="flex-shrink-0"
                @edit-stage="handleEditStage"
                @delete-stage="handleDeleteStage"
                @move-stage-left="handleMoveStageLeft"
                @move-stage-right="handleMoveStageRight"
                @edit-deal="openEditDealForm"
                @create-activity="openCreateActivityModal"
                @complete-activity="completeActivity"
                @delete-activity="deleteActivity"
                @open-activity-detail="openActivityDetailModal"
                @request-move="handleRequestMove"
              />
            </div>

            <!-- Estado vacío cuando no hay stages -->
            <div
              v-else
              class="flex flex-col items-center justify-center h-full text-center"
            >
              <div
                class="w-16 h-16 bg-n-slate-9/10 rounded-full flex items-center justify-center mb-4"
              >
                <svg
                  class="w-8 h-8 text-n-slate-9"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z"
                  />
                </svg>
              </div>
              <h3 class="text-lg font-medium text-n-slate-12 mb-2">
                No hay etapas en este pipeline
              </h3>
              <p class="text-n-slate-11 mb-6 max-w-md">
                Las etapas te ayudan a organizar tus deals por fase del proceso
                de ventas. Comienza creando tu primera etapa.
              </p>
              <button
                v-if="isAdmin"
                class="px-6 py-3 bg-n-brand hover:bg-n-brand/90 text-white rounded-lg font-medium transition-all cursor-pointer border-0 outline-none"
                @click="openNewStageForm"
              >
                + Crear Primera Etapa
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- ================= MOBILE ================= -->
    <template v-else>
      <!-- Bloque superior grisáceo: selector + KPIs + buscador -->
      <div class="flex-shrink-0 bg-n-solid-1 border-b-[0.5px] border-n-weak">
      <!-- 1. Header: el nombre del pipeline ES el selector -->
      <div class="px-4 pt-3 pb-2">
        <div class="flex items-center justify-between gap-2 min-h-[44px]">
          <div class="relative min-w-0 flex-1">
            <button
              class="flex items-center gap-1.5 min-w-0 max-w-full bg-transparent border-0 outline-none cursor-pointer p-0 text-left"
              @click.stop="showMobilePipelineMenu = !showMobilePipelineMenu"
            >
              <span class="text-lg font-medium text-n-slate-12 truncate">
                {{ pipeline?.name || "Pipeline" }}
              </span>
              <svg
                class="w-4 h-4 flex-shrink-0 text-n-slate-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <!-- Dropdown de pipelines -->
            <div
              v-if="showMobilePipelineMenu"
              class="absolute z-50 top-full left-0 mt-1 min-w-[220px] max-w-[80vw] bg-n-solid-2 border-[0.5px] border-n-weak rounded-xl shadow-lg overflow-hidden"
              @click.stop
            >
              <button
                v-for="p in pipelines"
                :key="p.id"
                class="w-full text-left px-4 min-h-[44px] text-sm border-0 outline-none cursor-pointer flex items-center transition-colors"
                :class="
                  p.id === selectedPipeline
                    ? 'text-n-brand bg-n-brand/10'
                    : 'text-n-slate-12 hover:bg-n-slate-9/5'
                "
                @click="selectMobilePipeline(p.id)"
              >
                {{ p.name }}
              </button>
            </div>
          </div>

          <!-- Menú de tres puntos: acciones secundarias -->
          <div v-if="isAdmin" class="relative flex-shrink-0">
            <button
              class="flex items-center justify-center w-11 h-11 -mr-2 rounded-lg text-n-slate-11 hover:bg-n-slate-9/10 border-0 outline-none cursor-pointer"
              @click.stop="showMobileHeaderMenu = !showMobileHeaderMenu"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                />
              </svg>
            </button>
            <div
              v-if="showMobileHeaderMenu"
              class="absolute z-50 top-full right-0 mt-1 w-52 bg-n-solid-2 border-[0.5px] border-n-weak rounded-xl shadow-lg overflow-hidden py-1"
              @click.stop
            >
              <button
                v-if="selectedPipeline"
                class="w-full text-left px-4 min-h-[44px] text-sm text-n-slate-12 hover:bg-n-slate-9/5 border-0 outline-none cursor-pointer flex items-center"
                @click="showMobileHeaderMenu = false; openNewStageForm()"
              >
                Nueva etapa
              </button>
              <button
                class="w-full text-left px-4 min-h-[44px] text-sm text-n-slate-12 hover:bg-n-slate-9/5 border-0 outline-none cursor-pointer flex items-center"
                @click="showMobileHeaderMenu = false; openNewPipelineForm()"
              >
                Nuevo pipeline
              </button>
              <button
                v-if="currentPipeline"
                class="w-full text-left px-4 min-h-[44px] text-sm text-n-slate-12 hover:bg-n-slate-9/5 border-0 outline-none cursor-pointer flex items-center"
                @click="showMobileHeaderMenu = false; editPipeline()"
              >
                Modificar pipeline
              </button>
            </div>
          </div>
        </div>
        <!-- Subtítulo -->
        <p class="text-xs text-n-slate-10 mt-0.5">
          {{ totals.dealCount }} negocios · {{ totals.uniqueLeadCount }} leads
        </p>
      </div>

      <!-- 2. KPI cards -->
      <div class="flex-shrink-0 grid grid-cols-2 gap-2 px-4 pb-3">
        <div class="rounded-xl border-[0.5px] border-n-weak bg-n-solid-2 px-3 py-2.5">
          <p class="text-xs text-n-slate-10">Total</p>
          <div class="mt-0.5 flex flex-wrap items-baseline gap-x-1.5">
            <template v-if="moneyEntries(totals.sumByCurrency).length">
              <span
                v-for="e in moneyEntries(totals.sumByCurrency)"
                :key="e.currency"
                class="flex items-baseline gap-1"
              >
                <span class="text-xl font-medium text-n-slate-12">{{ e.value }}</span>
                <span class="text-xs text-n-slate-10">{{ e.currency }}</span>
              </span>
            </template>
            <span v-else class="text-xl font-medium text-n-slate-11">—</span>
          </div>
        </div>
        <div class="rounded-xl border-[0.5px] border-n-weak bg-n-solid-2 px-3 py-2.5">
          <p class="text-xs text-n-slate-10">Ponderado</p>
          <div class="mt-0.5 flex flex-wrap items-baseline gap-x-1.5">
            <template v-if="moneyEntries(totals.weightedSumByCurrency).length">
              <span
                v-for="e in moneyEntries(totals.weightedSumByCurrency)"
                :key="e.currency"
                class="flex items-baseline gap-1"
              >
                <span class="text-xl font-medium text-n-brand">{{ e.value }}</span>
                <span class="text-xs text-n-slate-10">{{ e.currency }}</span>
              </span>
            </template>
            <span v-else class="text-xl font-medium text-n-slate-11">—</span>
          </div>
        </div>
      </div>

      <!-- 3. Buscador -->
      <div class="flex-shrink-0 px-4 pb-3">
        <div
          class="flex items-stretch h-11 rounded-lg border-[0.5px] border-n-weak bg-n-solid-2 overflow-hidden focus-within:border-n-slate-8 transition-colors"
        >
          <div class="flex items-center flex-1 min-w-0 gap-2.5 pl-3 pr-2">
            <svg
              class="w-[18px] h-[18px] flex-shrink-0 text-n-slate-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z"
              />
            </svg>
            <input
              :value="mobileQuery"
              type="text"
              placeholder="Título, contacto o teléfono"
              class="crm-search-input no-margin flex-1 min-w-0 h-5 bg-transparent outline-none border-0 p-0 m-0 text-sm leading-5 text-n-slate-12 placeholder-n-slate-10"
              @input="onMobileSearchInput"
            />
          </div>
          <button
            class="flex items-center justify-center w-11 flex-shrink-0 border-0 border-l-[0.5px] border-n-weak outline-none cursor-pointer transition-colors"
            :class="
              mobileFilterChips.length
                ? 'text-n-brand bg-n-brand/10'
                : 'text-n-slate-11 bg-transparent hover:bg-n-slate-9/5'
            "
            aria-label="Filtros"
            @click="openMobileFilterSheet"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M7 12h10M10 18h4"
              />
            </svg>
          </button>
        </div>

        <!-- Chips de filtros activos -->
        <div
          v-if="mobileFilterChips.length"
          class="flex gap-2 overflow-x-auto mt-2 -mb-0.5 pb-0.5"
          style="scrollbar-width: none"
        >
          <button
            v-for="chip in mobileFilterChips"
            :key="chip.key"
            class="flex-shrink-0 flex items-center gap-1 h-7 pl-3 pr-2 rounded-full text-xs bg-n-brand/10 text-n-brand border-0 outline-none cursor-pointer whitespace-nowrap"
            @click="clearMobileFilterChip(chip.key)"
          >
            {{ chip.label }}
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
      </div>
      <!-- /Bloque superior grisáceo -->

      <!-- 4. Tabs de etapas -->
      <div
        v-if="stageOrder.length > 0"
        class="flex-shrink-0 flex gap-2 px-3 py-1.5 overflow-x-auto border-b-[0.5px] border-n-weak"
        style="scrollbar-width: none"
      >
        <button
          v-for="(stageId, idx) in stageOrder"
          :key="stageId"
          class="flex-shrink-0 flex items-center gap-2 h-9 px-3.5 border-0 outline-none cursor-pointer whitespace-nowrap transition-all"
          :class="
            idx === activeMobileStageIndex
              ? 'rounded-full bg-n-brand text-white font-medium'
              : 'rounded-lg bg-transparent text-n-slate-11 hover:bg-n-slate-9/5'
          "
          @click="selectMobileStage(idx)"
        >
          <span
            class="w-2 h-2 rounded-full flex-shrink-0"
            :style="{
              backgroundColor:
                idx === activeMobileStageIndex
                  ? undefined
                  : stagesById[stageId].color || '#6B7280',
            }"
            :class="idx === activeMobileStageIndex ? 'bg-white' : ''"
          />
          <span class="text-sm">{{ stagesById[stageId].name }}</span>
          <span
            class="text-xs px-1.5 rounded-full leading-5 min-w-[20px] text-center"
            :class="
              idx === activeMobileStageIndex
                ? 'bg-white/25 text-white'
                : 'bg-n-slate-9/10 text-n-slate-10'
            "
          >
            {{ columnTotals(stageId).dealCount }}
          </span>
        </button>
      </div>

      <!-- Sub-barra de la etapa activa -->
      <div
        v-if="activeStage"
        class="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-1.5 bg-n-solid-1 text-xs"
      >
        <span class="text-n-slate-10 truncate">
          {{ activeStage.name }} · {{ activeStage.probability }}% prob.
        </span>
        <span class="text-n-slate-11 whitespace-nowrap flex-shrink-0">
          <template v-if="moneyEntries(activeStageMoneyTotals).length">
            <span
              v-for="(e, i) in moneyEntries(activeStageMoneyTotals)"
              :key="e.currency"
            >
              <template v-if="i > 0"> · </template>{{ e.currency }} {{ e.value }}
            </span>
          </template>
          <template v-else>—</template>
          <span class="text-n-slate-10">
            · pond.
            <template v-if="moneyEntries(activeStageWeightedTotals).length">
              <span
                v-for="e in moneyEntries(activeStageWeightedTotals)"
                :key="'w-' + e.currency"
                class="text-n-brand"
              >
                {{ e.value }}
              </span>
            </template>
            <template v-else>—</template>
          </span>
        </span>
        <button
          v-if="isAdmin"
          class="flex-shrink-0 flex items-center justify-center w-8 h-8 -my-1 -mr-2 rounded-lg text-n-slate-10 hover:bg-n-slate-9/10 border-0 outline-none cursor-pointer bg-transparent"
          :aria-label="`Acciones de la etapa ${activeStage.name}`"
          @click="openMobileStageSheet"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
            />
          </svg>
        </button>
      </div>

      <!-- 5. Lista de deals -->
      <div class="flex-1 min-h-0 overflow-y-auto bg-n-background">
        <!-- Loading skeleton -->
        <div v-if="isBoardLoading" class="px-3 py-3 space-y-2">
          <div
            v-for="n in 3"
            :key="'sk-' + n"
            class="px-3.5 py-3 rounded-xl border-[0.5px] border-n-weak bg-n-solid-2"
          >
            <div class="flex justify-between gap-3">
              <div class="h-3.5 w-40 rounded bg-n-slate-9/15 animate-pulse" />
              <div class="h-3.5 w-16 rounded bg-n-slate-9/15 animate-pulse" />
            </div>
            <div class="h-3 w-28 rounded bg-n-slate-9/10 animate-pulse mt-2" />
          </div>
        </div>

        <!-- Sin resultados por filtros -->
        <div
          v-else-if="activeStageDeals.length === 0 && hasActiveFilters"
          class="flex flex-col items-center justify-center text-center px-8 py-16"
        >
          <p class="text-sm text-n-slate-11">Ningún negocio coincide con los filtros.</p>
          <button
            class="mt-4 h-11 px-4 rounded-lg text-sm text-n-brand bg-n-brand/10 border-0 outline-none cursor-pointer"
            @click="clearAllMobileFilters"
          >
            Limpiar filtros
          </button>
        </div>

        <!-- Etapa vacía -->
        <div
          v-else-if="activeStageDeals.length === 0"
          class="flex flex-col items-center justify-center text-center px-8 py-16"
        >
          <p class="text-sm text-n-slate-11">Esta etapa no tiene negocios todavía.</p>
          <button
            class="mt-4 h-11 px-4 rounded-lg text-sm text-white bg-n-brand border-0 outline-none cursor-pointer"
            @click="openNewDealForm"
          >
            Crear negocio
          </button>
        </div>

        <!-- Cards de deals -->
        <div v-else class="px-3 py-3 space-y-2">
        <button
          v-for="deal in activeStageDeals"
          :key="deal.id"
          class="w-full text-left px-3.5 py-3 rounded-xl border-solid border-[0.5px] border-n-weak bg-n-solid-2 shadow-sm dark:shadow-none outline-none cursor-pointer active:bg-n-slate-9/5 transition-colors"
          @click="openEditDealForm(deal)"
        >
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ deal.title }}
            </span>
            <span
              class="text-sm whitespace-nowrap flex-shrink-0"
              :class="deal.amount_cents ? 'text-n-slate-12' : 'text-n-slate-10'"
            >
              {{ formatDealAmount(deal) }}
            </span>
          </div>
          <p class="text-xs text-n-slate-10 mt-0.5 truncate">
            {{ mobileOwnerName(deal) }}
            <template v-if="deal.created_at">
              · {{ formatDealAge(deal.created_at) }}
            </template>
          </p>
        </button>
        </div>
      </div>

      <!-- 6. Barra de acción inferior -->
      <div
        class="flex-shrink-0 flex items-center justify-between gap-3 px-4 h-14 border-t-[0.5px] border-n-weak bg-n-solid-2"
      >
        <span class="text-xs text-n-slate-10">
          Mostrando {{ activeStageDeals.length }} de {{ activeStageLoadedCount }}
        </span>
        <button
          v-if="selectedPipeline"
          class="flex items-center gap-1 h-11 px-4 text-sm font-medium text-n-brand border-0 outline-none cursor-pointer bg-transparent"
          @click="openNewDealForm"
        >
          + Negocio
        </button>
      </div>

      <!-- Backdrops para cerrar menús -->
      <div
        v-if="showMobilePipelineMenu || showMobileHeaderMenu"
        class="fixed inset-0 z-40"
        @click="showMobilePipelineMenu = false; showMobileHeaderMenu = false"
      />

      <!-- Bottom sheet de acciones de la etapa activa -->
      <div
        v-if="showMobileStageSheet && activeStage"
        class="fixed inset-0 z-[60] flex flex-col justify-end"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black1 backdrop-blur-[1px]"
          @click="showMobileStageSheet = false"
        />
        <div
          class="relative bg-n-solid-2 rounded-t-2xl border-t-[0.5px] border-n-weak flex flex-col"
        >
          <div class="px-4 py-3 border-b-[0.5px] border-n-weak">
            <p class="text-xs text-n-slate-10">Etapa</p>
            <h3 class="text-base font-medium text-n-slate-12 truncate">
              {{ activeStage.name }}
            </h3>
          </div>

          <div class="py-1">
            <button
              class="w-full flex items-center gap-3 px-4 min-h-[52px] text-sm text-n-slate-12 hover:bg-n-slate-9/5 border-0 outline-none cursor-pointer bg-transparent"
              @click="editActiveStage"
            >
              <svg class="w-5 h-5 flex-shrink-0 text-n-slate-11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Modificar etapa
            </button>

            <button
              :disabled="activeStagePosition <= 0"
              class="w-full flex items-center gap-3 px-4 min-h-[52px] text-sm text-n-slate-12 hover:bg-n-slate-9/5 border-0 outline-none cursor-pointer bg-transparent disabled:opacity-40 disabled:cursor-not-allowed"
              @click="moveActiveStage('left')"
            >
              <svg class="w-5 h-5 flex-shrink-0 text-n-slate-11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7 7-7M3 12h18"
                />
              </svg>
              Mover a la izquierda
            </button>

            <button
              :disabled="activeStagePosition === stageOrder.length - 1"
              class="w-full flex items-center gap-3 px-4 min-h-[52px] text-sm text-n-slate-12 hover:bg-n-slate-9/5 border-0 outline-none cursor-pointer bg-transparent disabled:opacity-40 disabled:cursor-not-allowed"
              @click="moveActiveStage('right')"
            >
              <svg class="w-5 h-5 flex-shrink-0 text-n-slate-11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7-7 7M21 12H3"
                />
              </svg>
              Mover a la derecha
            </button>

            <button
              class="w-full flex items-center gap-3 px-4 min-h-[52px] text-sm text-n-ruby-11 hover:bg-n-ruby-10/5 border-0 outline-none cursor-pointer bg-transparent"
              @click="deleteActiveStage"
            >
              <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Eliminar etapa
            </button>
          </div>

          <div class="px-4 py-3 border-t-[0.5px] border-n-weak">
            <button
              class="w-full h-11 rounded-lg text-sm text-n-slate-11 bg-n-slate-9/10 border-0 outline-none cursor-pointer"
              @click="showMobileStageSheet = false"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>

      <!-- Bottom sheet de filtros -->
      <div
        v-if="showMobileFilterSheet"
        class="fixed inset-0 z-[60] flex flex-col justify-end"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black1 backdrop-blur-[1px]"
          @click="showMobileFilterSheet = false"
        />
        <div
          class="relative bg-n-solid-2 rounded-t-2xl border-t-[0.5px] border-n-weak max-h-[80vh] flex flex-col"
        >
          <div class="flex items-center justify-between px-4 py-3 border-b-[0.5px] border-n-weak">
            <h3 class="text-base font-medium text-n-slate-12">Filtros</h3>
            <button
              class="w-9 h-9 flex items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-slate-9/10 border-0 outline-none cursor-pointer"
              @click="showMobileFilterSheet = false"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-4 py-3 space-y-5">
            <!-- Responsable -->
            <div>
              <p class="text-xs text-n-slate-10 mb-2">Responsable</p>
              <p v-if="pipelineOwners.length === 0" class="text-sm text-n-slate-10">
                Sin responsables cargados.
              </p>
              <div v-else class="flex flex-wrap gap-2">
                <button
                  v-for="owner in pipelineOwners"
                  :key="owner.id"
                  class="h-9 px-3 rounded-lg text-sm border-[0.5px] outline-none cursor-pointer transition-colors"
                  :class="
                    mobileFilterDraft.ownerIds.includes(owner.id)
                      ? 'border-n-brand text-n-brand bg-n-brand/10'
                      : 'border-n-weak text-n-slate-11 bg-n-solid-1'
                  "
                  @click="toggleDraftOwner(owner.id)"
                >
                  {{ owner.name }}
                </button>
              </div>
            </div>

            <!-- Fecha de creación -->
            <div>
              <p class="text-xs text-n-slate-10 mb-2">Fecha de creación</p>
              <div class="flex items-center gap-2">
                <input
                  v-model="mobileFilterDraft.dateFrom"
                  type="date"
                  class="flex-1 h-10 px-3 rounded-lg border-[0.5px] border-n-weak bg-n-solid-1 text-sm text-n-slate-12 outline-none"
                />
                <span class="text-n-slate-10 text-sm">a</span>
                <input
                  v-model="mobileFilterDraft.dateTo"
                  type="date"
                  class="flex-1 h-10 px-3 rounded-lg border-[0.5px] border-n-weak bg-n-solid-1 text-sm text-n-slate-12 outline-none"
                />
              </div>
            </div>

            <!-- Monto -->
            <div>
              <p class="text-xs text-n-slate-10 mb-2">Monto</p>
              <div class="flex items-center gap-2">
                <input
                  v-model="mobileFilterDraft.amountMin"
                  type="number"
                  min="0"
                  placeholder="Mínimo"
                  class="flex-1 h-10 px-3 rounded-lg border-[0.5px] border-n-weak bg-n-solid-1 text-sm text-n-slate-12 outline-none placeholder-n-slate-10"
                />
                <span class="text-n-slate-10 text-sm">a</span>
                <input
                  v-model="mobileFilterDraft.amountMax"
                  type="number"
                  min="0"
                  placeholder="Máximo"
                  class="flex-1 h-10 px-3 rounded-lg border-[0.5px] border-n-weak bg-n-solid-1 text-sm text-n-slate-12 outline-none placeholder-n-slate-10"
                />
              </div>
            </div>
          </div>

          <div class="flex gap-3 px-4 py-3 border-t-[0.5px] border-n-weak">
            <button
              class="flex-1 h-11 rounded-lg text-sm text-n-slate-11 border-[0.5px] border-n-weak bg-n-solid-1 outline-none cursor-pointer"
              @click="clearAllMobileFilters(); showMobileFilterSheet = false"
            >
              Limpiar
            </button>
            <button
              class="flex-1 h-11 rounded-lg text-sm font-medium text-white bg-n-brand border-0 outline-none cursor-pointer"
              @click="applyMobileFilters"
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal Formulario Crear Pipeline -->
    <Modal
      v-model:show="showNewPipelineForm"
      :close-on-backdrop-click="true"
      :show-close-button="true"
      :on-close="closeNewPipelineForm"
    >
      <div class="!px-6 !py-4">
        <h3 class="text-lg font-semibold mb-6 text-n-slate-12">
          {{
            isEditingPipeline ? "Modificar Pipeline" : "Crear Nuevo Pipeline"
          }}
        </h3>

        <form class="space-y-6 !p-0" @submit.prevent="createPipeline">
          <!-- Campo Nombre -->
          <div>
            <Input
              id="pipeline-name"
              v-model="pipelineForm.name"
              type="text"
              label="Nombre del Pipeline"
              placeholder="Ej: Pipeline de Ventas"
              :disabled="isCreatingPipeline"
              :message="pipelineNameError"
              :message-type="pipelineNameError ? 'error' : 'info'"
            />
          </div>

          <!-- Campo Visibilidad -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-3">
              Visibilidad de Deals
            </label>
            <div class="space-y-3">
              <label class="flex cursor-pointer group">
                <input
                  v-model="pipelineForm.deals_visibility"
                  type="radio"
                  value="team"
                  class="sr-only"
                  :disabled="isCreatingPipeline"
                />
                <div
                  class="relative w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-200 flex-shrink-0 mt-1"
                  :class="[
                    pipelineForm.deals_visibility === 'team'
                      ? 'border-n-brand bg-n-brand'
                      : 'border-n-slate-6 bg-white dark:bg-n-solid-2 group-hover:border-n-brand/50',
                  ]"
                >
                  <div
                    v-if="pipelineForm.deals_visibility === 'team'"
                    class="w-2 h-2 rounded-full bg-white"
                  />
                </div>
                <div class="flex-1">
                  <div class="font-medium text-n-slate-12 leading-5">
                    Equipo (Público)
                  </div>
                  <div class="text-sm text-n-slate-11 mt-1 leading-relaxed">
                    Todos los miembros del equipo pueden ver todos los deals
                  </div>
                </div>
              </label>

              <label class="flex cursor-pointer group">
                <input
                  v-model="pipelineForm.deals_visibility"
                  type="radio"
                  value="restricted"
                  class="sr-only"
                  :disabled="isCreatingPipeline"
                />
                <div
                  class="relative w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-200 flex-shrink-0 mt-1"
                  :class="[
                    pipelineForm.deals_visibility === 'restricted'
                      ? 'border-n-brand bg-n-brand'
                      : 'border-n-slate-6 bg-white dark:bg-n-solid-2 group-hover:border-n-brand/50',
                  ]"
                >
                  <div
                    v-if="pipelineForm.deals_visibility === 'restricted'"
                    class="w-2 h-2 rounded-full bg-white"
                  />
                </div>
                <div class="flex-1">
                  <div class="font-medium text-n-slate-12 leading-5">
                    Restringido (Privado)
                  </div>
                  <div class="text-sm text-n-slate-11 mt-1 leading-relaxed">
                    Solo el creador de cada deal puede verlo
                  </div>
                </div>
              </label>
            </div>
          </div>

          <!-- Sección de Equipos -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-3">
              Equipos Asignados
              <span
                v-if="pipelineForm.team_ids.length > 0"
                class="text-n-slate-11"
                >({{ pipelineForm.team_ids.length }} seleccionados)</span>
            </label>

            <!-- Lista de equipos disponibles -->
            <div
              class="max-h-32 overflow-y-auto border border-n-weak rounded-md bg-white dark:bg-n-solid-2"
            >
              <div
                v-if="getTeams.length === 0"
                class="p-3 text-n-slate-11 text-sm text-center"
              >
                No hay equipos disponibles
              </div>
              <div v-else>
                <label
                  v-for="team in getTeams"
                  :key="team.id"
                  class="flex items-center p-3 hover:bg-n-slate-9/5 cursor-pointer border-b border-n-weak last:border-b-0"
                  :class="{ 'bg-n-brand/5': isTeamSelected(team.id) }"
                >
                  <input
                    type="checkbox"
                    :checked="isTeamSelected(team.id)"
                    class="sr-only"
                    :disabled="isCreatingPipeline"
                    @change="toggleTeamSelection(team.id)"
                  />
                  <div class="flex items-center w-full">
                    <div
                      class="w-4 h-4 rounded border-2 border-n-brand mr-3 flex items-center justify-center"
                      :class="
                        isTeamSelected(team.id)
                          ? 'bg-n-brand'
                          : 'bg-white dark:bg-n-solid-2'
                      "
                    >
                      <svg
                        v-if="isTeamSelected(team.id)"
                        class="w-2.5 h-2.5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <div class="font-medium text-n-slate-12">
                        {{ team.name }}
                      </div>
                      <div
                        v-if="team.description"
                        class="text-sm text-n-slate-11"
                      >
                        {{ team.description }}
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Equipos seleccionados (resumen) -->
            <div v-if="pipelineForm.team_ids.length > 0" class="mt-2">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="team in getSelectedTeams()"
                  :key="team.id"
                  class="inline-flex items-center px-2 py-1 bg-n-brand/10 text-n-brand text-xs rounded-full"
                >
                  {{ team.name }}
                  <button
                    type="button"
                    class="ml-1 text-n-brand hover:text-n-brand/80"
                    :disabled="isCreatingPipeline"
                    @click="toggleTeamSelection(team.id)"
                  >
                    <svg
                      class="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </span>
              </div>
            </div>
          </div>

          <!-- Botones -->
          <div class="flex justify-end space-x-3 pt-4 border-t border-n-weak">
            <button
              type="button"
              class="px-4 py-2 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all"
              :disabled="isCreatingPipeline"
              @click="closeNewPipelineForm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-n-brand hover:brightness-110 text-white rounded-lg cursor-pointer border-0 outline-none transition-all whitespace-nowrap min-w-fit flex items-center"
              :disabled="isCreatingPipeline || !pipelineForm.name.trim()"
            >
              <span
                v-if="isCreatingPipeline"
                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
              />
              {{
                isCreatingPipeline
                  ? isEditingPipeline
                    ? "Actualizando..."
                    : "Creando..."
                  : isEditingPipeline
                    ? "Actualizar Pipeline"
                    : "Crear Pipeline"
              }}
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <!-- Modal de Confirmación de Eliminación -->
    <div
      v-if="showDeleteConfirmationModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white dark:bg-n-solid-3 rounded-lg p-6 w-96 max-w-md shadow-lg"
      >
        <h3 class="text-lg font-semibold mb-4 text-n-ruby-10">
          Eliminar pipeline "{{ currentPipeline?.name }}"
        </h3>

        <!-- Loading de estadísticas -->
        <div v-if="deleteConfirmation.isLoadingStats" class="text-center py-4">
          <div
            class="inline-block w-6 h-6 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
          />
          <p class="text-n-slate-11 mt-2">Cargando información...</p>
        </div>

        <!-- Contenido del modal -->
        <div v-else-if="deleteConfirmation.stats" class="space-y-4">
          <!-- Advertencia -->
          <div class="bg-n-ruby-10/5 border border-n-ruby-10/20 rounded-lg p-3">
            <p class="text-n-slate-12 text-sm mb-2">
              <strong>⚠️ Esta acción es irreversible.</strong>
            </p>
            <p class="text-n-slate-11 text-sm">Se eliminarán:</p>
            <ul
              class="text-n-slate-12 text-sm mt-1 space-y-1 list-disc list-inside"
            >
              <li>
                {{ deleteConfirmation.stats.stages_count }} etapa{{
                  deleteConfirmation.stats.stages_count !== 1 ? "s" : ""
                }}
              </li>
              <li>
                {{ deleteConfirmation.stats.deals_count }} deal{{
                  deleteConfirmation.stats.deals_count !== 1 ? "s" : ""
                }}
              </li>
              <li>
                {{ deleteConfirmation.stats.activities_count }} actividad{{
                  deleteConfirmation.stats.activities_count !== 1 ? "es" : ""
                }}
              </li>
            </ul>
          </div>

          <!-- Campo de confirmación -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Escribe '<strong>{{ currentPipeline?.name }}</strong>' para confirmar:
            </label>
            <input
              v-model="deleteConfirmation.confirmationText"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-n-ruby-10 focus:border-n-ruby-10 bg-white dark:bg-n-solid-2 text-n-slate-12"
              :placeholder="currentPipeline?.name"
              :disabled="isDeletingPipeline"
            />
          </div>

          <!-- Botones -->
          <div class="flex justify-end space-x-3 pt-4 border-t border-n-weak">
            <button
              type="button"
              class="px-4 py-2 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all"
              :disabled="isDeletingPipeline"
              @click="closeDeleteConfirmationModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="px-4 py-2 bg-n-ruby-10 hover:brightness-110 text-white rounded-lg cursor-pointer border-0 outline-none transition-all whitespace-nowrap min-w-fit flex items-center"
              :disabled="
                isDeletingPipeline ||
                deleteConfirmation.confirmationText !== currentPipeline?.name
              "
              @click="confirmDeletePipeline"
            >
              <span
                v-if="isDeletingPipeline"
                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
              />
              {{
                isDeletingPipeline
                  ? "Eliminando..."
                  : "Eliminar Permanentemente"
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Formulario Crear Nueva Etapa -->
    <Modal
      v-model:show="showNewStageForm"
      :close-on-backdrop-click="true"
      :show-close-button="true"
      :on-close="closeNewStageForm"
    >
      <div class="!px-6 !py-4">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ isEditingStage ? "Modificar Etapa" : "Nueva Etapa" }}
        </h3>

        <form
          class="!p-0 flex flex-col"
          @submit.prevent="isEditingStage ? updateStage() : createStage()"
        >
          <div class="pr-2 space-y-4">
            <!-- Campo Nombre -->
            <div>
              <Input
                id="stage-name"
                v-model="stageForm.name"
                type="text"
                label="Nombre de la Etapa"
                placeholder="Ej: Prospecto, Propuesta, Negociación..."
                :disabled="isCreatingStage"
                :maxlength="30"
                :message="stageNameError"
                :message-type="stageNameError ? 'error' : 'info'"
              />
              <p class="text-xs text-n-slate-11 mt-1">Máximo 30 caracteres</p>
            </div>

            <!-- Campo Probabilidad -->
            <div>
              <div class="relative">
                <Input
                  id="stage-probability"
                  v-model.number="stageForm.probability"
                  type="number"
                  label="Probabilidad de Cierre"
                  placeholder="0"
                  custom-input-class="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  :disabled="
                    isCreatingStage ||
                    stageForm.stage_type === 'won' ||
                    stageForm.stage_type === 'lost' ||
                    stageForm.stage_type === 'canceled'
                  "
                  :message="stageProbabilityError"
                  :message-type="stageProbabilityError ? 'error' : 'info'"
                />
                <span class="absolute right-3 top-9 text-n-slate-11">%</span>
              </div>
              <p class="text-xs text-n-slate-11 mt-1">
                Porcentaje de probabilidad que los deals en esta etapa se
                cierren exitosamente (0-100)
              </p>
            </div>

            <!-- Campo Tipo de Etapa -->
            <div>
              <Select
                id="stage-type"
                v-model="stageForm.stage_type"
                :options="stageTypeOptions"
                option-value="value"
                option-label="name"
                :label="$t('CRM.STAGES.FORM.TYPE')"
                :disabled="isCreatingStage"
                required
              />
              <p class="text-xs text-n-slate-11 mt-1">
                Los deals en etapas especiales (ganado, perdido, etc.)
                actualizarán su estado automáticamente
              </p>
            </div>

            <!-- Checkbox para habilitar formulario (solo si stage_type !== 'regular') -->
            <div
              v-if="stageForm.stage_type !== 'regular'"
              class="bg-n-slate-9/5 rounded-lg p-3"
            >
              <label class="flex items-start">
                <input
                  v-model="stageForm.form_config.enabled"
                  type="checkbox"
                  class="mt-1 mr-2"
                  :disabled="isCreatingStage"
                />
                <div>
                  <span class="text-sm font-medium text-n-slate-12">{{
                    $t("CRM.STAGES.FORM.REQUIRE_FORM")
                  }}</span>
                  <p class="text-xs text-n-slate-11 mt-1">
                    Si está habilitado, se mostrará un formulario al mover deals
                    a esta etapa
                  </p>
                </div>
              </label>
            </div>

            <!-- Editor de configuración de formulario (si enabled) -->
            <div
              v-if="
                stageForm.form_config.enabled &&
                stageForm.stage_type !== 'regular'
              "
            >
              <StageFormBuilder
                v-model="stageForm.form_config"
                :stage-type="stageForm.stage_type"
              />
            </div>
          </div>

          <!-- Información automática -->
          <div class="bg-n-slate-9/5 rounded-lg p-3 mt-4">
            <p class="text-sm text-n-slate-11">
              <strong>Posición:</strong>
              <span v-if="isEditingStage">
                {{ stageForm.position }} (posición actual)
              </span>
              <span v-else>
                {{ stageOrder.length + 1 }} (automática, al final del pipeline)
              </span>
            </p>
          </div>

          <!-- Botones -->
          <div
            class="flex justify-end space-x-3 pt-4 mt-4 border-t border-n-weak"
          >
            <button
              type="button"
              class="px-4 py-2 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all"
              :disabled="isCreatingStage"
              @click="closeNewStageForm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-n-brand hover:brightness-110 text-white rounded-lg cursor-pointer border-0 outline-none transition-all whitespace-nowrap min-w-fit flex items-center"
              :disabled="isCreatingStage || !stageForm.name.trim()"
            >
              <div
                v-if="isCreatingStage"
                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
              />
              {{
                isCreatingStage
                  ? isEditingStage
                    ? "Actualizando..."
                    : "Creando..."
                  : isEditingStage
                    ? "Actualizar Etapa"
                    : "Crear Etapa"
              }}
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <!-- Modal Confirmación Eliminar Etapa -->
    <div
      v-if="showDeleteStageModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white dark:bg-n-solid-3 rounded-lg p-6 w-96 max-w-md shadow-lg"
      >
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          Confirmar Eliminación
        </h3>

        <p class="text-n-slate-11 mb-6">
          ¿Estás seguro que quieres eliminar la etapa "<strong>{{
            stageToDelete?.name
          }}</strong>"? <br /><br />
          <span class="text-n-ruby-10 text-sm">
            Esta acción eliminará permanentemente la etapa y todos sus deals y
            actividades asociadas.
          </span>
        </p>

        <div class="flex space-x-3 justify-end">
          <button
            type="button"
            class="px-4 py-2 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all"
            :disabled="isDeletingStage"
            @click="closeDeleteStageModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-n-ruby-10 hover:brightness-110 text-white rounded-lg cursor-pointer border-0 outline-none transition-all whitespace-nowrap min-w-fit flex items-center"
            :disabled="isDeletingStage"
            @click="confirmDeleteStage"
          >
            <svg
              v-if="isDeletingStage"
              class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {{ isDeletingStage ? "Eliminando..." : "Eliminar Etapa" }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Formulario Crear Nuevo Negocio -->
  <Modal
    v-model:show="showNewDealForm"
    :close-on-backdrop-click="true"
    :show-close-button="true"
    :on-close="closeNewDealForm"
    size="medium"
  >
    <div class="!px-6 !py-4">
      <h3 class="text-lg font-semibold mb-6 text-n-slate-12">Nuevo Negocio</h3>

      <div class="space-y-6 !p-0">
        <!-- Nombre del negocio -->
        <div>
          <Input
            id="deal-title"
            v-model="dealForm.title"
            type="text"
            label="Nombre del negocio"
            placeholder="Ej: Venta de software CRM"
            :disabled="isCreatingDeal"
            :message="dealTitleError"
            :message-type="dealTitleError ? 'error' : 'info'"
          />
        </div>

        <!-- Pipeline y Etapa -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Pipeline -->
          <div>
            <Select
              v-model="dealForm.pipeline_id"
              :options="pipelines"
              label="Pipeline"
              placeholder="Seleccionar pipeline"
              :disabled="isCreatingDeal"
              required
              @change="onPipelineChange"
            />
          </div>

          <!-- Etapa -->
          <div>
            <Select
              v-model="dealForm.stage_id"
              :options="availableStagesFormatted"
              label="Etapa"
              placeholder="Seleccionar etapa"
              :disabled="isCreatingDeal || !dealForm.pipeline_id"
              required
            />
          </div>
        </div>

        <!-- Propietario y Contacto Principal -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Propietario -->
          <div>
            <label
              for="deal-owner"
              class="block text-sm font-medium text-n-slate-12 mb-2"
            >
              Propietario del negocio
            </label>
            <SearchableSelect
              v-model="dealForm.owner_id"
              :options="availableAgents"
              :placeholder="
                availableAgents.length === 0
                  ? 'Sin agentes disponibles'
                  : 'Seleccionar propietario'
              "
              :disabled="isCreatingDeal || !dealForm.pipeline_id"
            />
            <p
              v-if="availableAgents.length === 0 && dealForm.pipeline_id"
              class="text-xs text-amber-600 dark:text-amber-500 mt-1"
            >
              Este pipeline no tiene equipos asignados. Puedes continuar sin
              propietario o configurar equipos primero.
            </p>
          </div>

          <!-- Contacto Principal -->
          <div>
            <label
              for="deal-contact"
              class="block text-sm font-medium text-n-slate-12 mb-2"
            >
              Contacto principal
            </label>
            <SearchableContactSelect
              v-model="dealForm.primary_contact_id"
              placeholder="Seleccionar contacto principal"
              :disabled="isCreatingDeal"
            />
          </div>
        </div>

        <!-- Conversación vinculada -->
        <div v-if="dealForm.primary_contact_id">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Conversación vinculada
          </label>
          <Select
            v-model="dealForm.conversation_id"
            :options="contactConversations"
            :placeholder="
              isLoadingConversations ? 'Cargando...' : 'Sin conversación'
            "
            option-value="id"
            option-label="name"
            :disabled="isCreatingDeal || isLoadingConversations"
          />
        </div>

        <!-- Productos del negocio -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label
              for="deal-product"
              class="block text-sm font-medium text-n-slate-12"
            >
              Productos del negocio
            </label>
          </div>

          <CrmMultiSelectSearch
            v-model="dealForm.product_ids"
            :options="filteredProductsByCompatibleCurrency"
            placeholder="Seleccionar productos"
          />
          <p v-if="hasProductsSelected" class="text-xs text-n-slate-11 mt-1">
            {{ selectedProducts.length }} producto(s) seleccionado(s) - Moneda:
            {{ selectedProductsCurrency }}
          </p>
        </div>

        <!-- Monto y Moneda -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Monto -->
          <div>
            <Input
              id="deal-amount"
              v-model="dealForm.amount"
              type="number"
              :label="
                hasProductsSelected
                  ? 'Monto (calculado automáticamente)'
                  : 'Monto'
              "
              placeholder="Ej: 1000.50"
              custom-input-class="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              :disabled="isCreatingDeal || isAmountReadonly"
              :message="dealAmountError"
              :message-type="dealAmountError ? 'error' : 'info'"
            />
            <p
              v-if="hasProductsSelected"
              class="text-xs text-blue-600 dark:text-blue-400 mt-1"
            >
              ℹ️ Monto calculado automáticamente desde productos
            </p>
          </div>

          <!-- Moneda -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Moneda
              <span
                v-if="hasProductsSelected"
                class="text-xs text-n-slate-11 font-normal ml-1"
                >(según productos)</span>
            </label>
            <Select
              v-model="dealForm.currency"
              :options="availableCurrenciesFormatted"
              option-value="code"
              option-label="name"
              placeholder="Seleccionar moneda"
              :disabled="isCreatingDeal || isCurrencyReadonly"
            />
            <p
              v-if="hasProductsSelected"
              class="text-xs text-blue-600 dark:text-blue-400 mt-1"
            >
              ℹ️ Moneda determinada por productos
            </p>
          </div>
        </div>

        <!-- Método de Pago y Fecha de cierre estimada -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Método de Pago -->
          <div>
            <label
              for="deal-payment-method"
              class="block text-sm font-medium text-n-slate-12 mb-2"
            >
              Métodos de pago
            </label>
            <CrmMultiSelectSearch
              v-model="dealForm.payment_method_ids"
              :options="paymentMethodOptions"
              placeholder="Seleccionar métodos de pago"
            />
          </div>

          <!-- Fecha de cierre estimada -->
          <div>
            <label
              for="deal-close-date"
              class="block text-sm font-medium text-n-slate-12 mb-2"
            >
              Fecha de cierre estimada
            </label>
            <input
              id="deal-close-date"
              v-model="dealForm.expected_close_date"
              type="date"
              class="w-full px-4 py-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 text-sm"
              :disabled="isCreatingDeal"
            />
          </div>
        </div>

        <!-- FORK: Etiquetas del deal -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Etiquetas
          </label>
          <CrmMultiSelectSearch
            v-model="dealForm.labels"
            :options="dealLabelOptions"
            placeholder="Seleccionar etiquetas"
          />
        </div>

        <!-- Botones -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-n-weak">
          <button
            type="button"
            class="px-6 py-3 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all font-medium"
            :disabled="isCreatingDeal"
            @click="closeNewDealForm"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="px-6 py-3 bg-n-brand hover:brightness-110 text-white rounded-lg cursor-pointer border-0 outline-none transition-all whitespace-nowrap min-w-fit flex items-center font-medium"
            :disabled="isCreatingDeal || !dealForm.title.trim()"
            @click="createDeal"
          >
            <div
              v-if="isCreatingDeal"
              class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
            />
            {{ isCreatingDeal ? "Creando..." : "Crear Negocio" }}
          </button>
        </div>
      </div>
    </div>
  </Modal>

  <!-- Sidebar Editar Negocio -->
  <transition name="slide">
    <div
      v-if="showEditDealForm"
      class="fixed top-0 right-0 h-full bg-white dark:bg-n-solid-3 shadow-xl z-50 overflow-y-auto"
      :class="isMobile ? 'w-full' : 'w-[420px]'"
    >
      <!-- Header -->
      <div
        class="p-4 border-b border-n-weak flex items-center"
        :class="isMobile ? 'flex-row-reverse justify-end gap-3' : 'justify-between'"
      >
        <h3 class="text-lg font-semibold text-n-slate-12">Editar Negocio</h3>
        <button
          class="text-n-slate-11 hover:text-n-slate-12"
          :class="isMobile ? 'text-xl leading-none' : ''"
          @click="closeEditDealForm"
        >
          {{ isMobile ? "←" : "✕" }}
        </button>
      </div>

      <!-- Navigation chips -->
      <div
        v-if="
          editingDeal &&
          (editingDeal.primary_contact_id || editingDeal.conversation_id)
        "
        class="px-4 py-2 border-b border-n-weak flex flex-wrap gap-2"
      >
        <router-link
          v-if="editingDeal.primary_contact_id"
          :to="{
            name: 'contacts_edit',
            params: { accountId, contactId: editingDeal.primary_contact_id },
          }"
          target="_blank"
          class="inline-flex items-center gap-1 px-3 py-1 bg-n-brand/10 hover:bg-n-brand/20 text-n-brand rounded-full text-xs font-medium transition-colors no-underline"
        >
          <svg
            class="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          Ver contacto
        </router-link>
        <router-link
          v-if="editingDeal.conversation_id"
          :to="{
            name: 'inbox_conversation',
            params: { accountId, conversation_id: editingDeal.conversation_id },
          }"
          target="_blank"
          class="inline-flex items-center gap-1 px-3 py-1 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400 rounded-full text-xs font-medium transition-colors no-underline"
        >
          <svg
            class="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          Ver conversación #{{ editingDeal.conversation_id }}
        </router-link>
      </div>

      <!-- Contenido del formulario -->
      <div class="p-6">
        <div class="space-y-6">
          <!-- Nombre del negocio -->
          <div>
            <label
              for="edit-deal-title"
              class="block text-sm font-medium text-n-slate-12 mb-2"
            >
              Nombre del negocio <span class="text-n-ruby-10">*</span>
            </label>
            <input
              id="edit-deal-title"
              v-model="dealForm.title"
              type="text"
              placeholder="Ej: Venta de software CRM"
              class="w-full px-4 py-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 text-sm"
              :disabled="isUpdatingDeal"
              required
            />
          </div>

          <!-- Pipeline y Etapa -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label
                for="edit-deal-pipeline"
                class="block text-sm font-medium text-n-slate-12 mb-2"
              >
                Pipeline <span class="text-n-ruby-10">*</span>
              </label>
              <select
                id="edit-deal-pipeline"
                v-model="dealForm.pipeline_id"
                class="w-full px-4 py-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 text-sm appearance-none cursor-pointer"
                :disabled="isUpdatingDeal"
                required
                @change="onPipelineChange"
              >
                <option value="">Seleccionar pipeline</option>
                <option
                  v-for="pipeline in pipelines"
                  :key="pipeline.id"
                  :value="pipeline.id"
                >
                  {{ pipeline.name }}
                </option>
              </select>
            </div>

            <div>
              <label
                for="edit-deal-stage"
                class="block text-sm font-medium text-n-slate-12 mb-2"
              >
                Etapa <span class="text-n-ruby-10">*</span>
              </label>
              <select
                id="edit-deal-stage"
                v-model="dealForm.stage_id"
                class="w-full px-4 py-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 text-sm appearance-none cursor-pointer"
                :disabled="isUpdatingDeal || !dealForm.pipeline_id"
                required
              >
                <option value="">Seleccionar etapa</option>
                <option
                  v-for="stage in availableStages"
                  :key="stage.id"
                  :value="stage.id"
                >
                  {{ stage.name }} ({{ stage.probability }}%)
                </option>
              </select>
            </div>
          </div>

          <!-- Propietario y Contacto -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label
                for="edit-deal-owner"
                class="block text-sm font-medium text-n-slate-12 mb-2"
              >
                Propietario del negocio
              </label>
              <SearchableSelect
                v-model="dealForm.owner_id"
                :options="availableAgents"
                placeholder="Seleccionar propietario"
                :disabled="isUpdatingDeal || !dealForm.pipeline_id"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                Contacto principal
              </label>
              <SearchableContactSelect
                v-model="dealForm.primary_contact_id"
                placeholder="Seleccionar contacto principal"
                :disabled="isUpdatingDeal"
              />
            </div>
          </div>

          <!-- Conversación vinculada -->
          <div v-if="dealForm.primary_contact_id">
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Conversación vinculada
            </label>
            <Select
              v-model="dealForm.conversation_id"
              :options="contactConversations"
              :placeholder="
                isLoadingConversations ? 'Cargando...' : 'Sin conversación'
              "
              option-value="id"
              option-label="name"
              :disabled="isUpdatingDeal || isLoadingConversations"
            />
          </div>

          <!-- Productos del negocio -->
          <div>
            <label
              for="edit-deal-product"
              class="block text-sm font-medium text-n-slate-12 mb-2"
            >
              Productos del negocio
            </label>
            <CrmMultiSelectSearch
              v-model="dealForm.product_ids"
              :options="filteredProductsByCompatibleCurrency"
              placeholder="Seleccionar productos"
            />
          </div>

          <!-- Monto y Moneda -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label
                for="edit-deal-amount"
                class="block text-sm font-medium text-n-slate-12 mb-2"
              >
                Monto
                <span
                  v-if="hasProductsSelected"
                  class="text-xs text-n-slate-11 font-normal ml-1"
                  >(calculado automáticamente)</span>
              </label>
              <input
                id="edit-deal-amount"
                v-model="dealForm.amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="Ej: 1000.50"
                class="w-full px-4 py-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 text-sm"
                :class="{
                  'bg-n-slate-3/30 cursor-not-allowed': isAmountReadonly,
                }"
                :disabled="isUpdatingDeal || isAmountReadonly"
                :readonly="isAmountReadonly"
              />
              <p
                v-if="hasProductsSelected"
                class="text-xs text-blue-600 dark:text-blue-400 mt-1"
              >
                ℹ️ Monto calculado automáticamente desde productos
              </p>
            </div>

            <div>
              <label
                for="edit-deal-currency"
                class="block text-sm font-medium text-n-slate-12 mb-2"
              >
                Moneda
                <span
                  v-if="hasProductsSelected"
                  class="text-xs text-n-slate-11 font-normal ml-1"
                  >(según productos)</span>
              </label>
              <select
                id="edit-deal-currency"
                v-model="dealForm.currency"
                class="w-full px-4 py-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 text-sm appearance-none cursor-pointer"
                :class="{
                  'bg-n-slate-3/30 cursor-not-allowed': isCurrencyReadonly,
                }"
                :disabled="isUpdatingDeal || isCurrencyReadonly"
              >
                <option
                  v-for="currency in availableCurrencies"
                  :key="currency.code"
                  :value="currency.code"
                >
                  {{ currency.code }} - {{ currency.name }}
                </option>
              </select>
              <p
                v-if="hasProductsSelected"
                class="text-xs text-blue-600 dark:text-blue-400 mt-1"
              >
                ℹ️ Moneda determinada por productos
              </p>
            </div>
          </div>

          <!-- Método de Pago y Fecha cierre -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Método de Pago -->
            <div>
              <label
                for="edit-deal-payment-method"
                class="block text-sm font-medium text-n-slate-12 mb-2"
              >
                Métodos de pago
              </label>
              <CrmMultiSelectSearch
                v-model="dealForm.payment_method_ids"
                :options="paymentMethodOptions"
                placeholder="Seleccionar métodos de pago"
              />
            </div>

            <!-- Fecha cierre -->
            <div>
              <label
                for="edit-deal-close-date"
                class="block text-sm font-medium text-n-slate-12 mb-2"
              >
                Fecha de cierre estimada
              </label>
              <input
                id="edit-deal-close-date"
                v-model="dealForm.expected_close_date"
                type="date"
                class="w-full px-4 py-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 text-sm"
                :disabled="isUpdatingDeal"
              />
            </div>
          </div>

          <!-- FORK: Etiquetas del deal -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Etiquetas
            </label>
            <CrmMultiSelectSearch
              v-model="dealForm.labels"
              :options="dealLabelOptions"
              placeholder="Seleccionar etiquetas"
            />
          </div>

          <!-- Historial de cambios de etapa -->
          <DealStageAttributes
            v-if="editingDeal"
            :deal="editingDeal"
            @close="closeEditDealForm"
          />

          <!-- Botones -->
          <div
            class="flex justify-between items-center pt-4 border-t border-n-weak"
          >
            <button
              v-if="canDeleteDeal"
              type="button"
              class="px-4 py-2 text-sm font-medium min-w-fit text-red-600 bg-white hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg cursor-pointer border-0 outline-none transition-all flex items-center"
              :disabled="isUpdatingDeal || isDeletingDeal"
              @click.stop="showDeleteDealConfirmation"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Eliminar
            </button>
            <div v-else />

            <div class="flex space-x-3">
              <button
                type="button"
                class="px-4 py-2 bg-n-brand hover:brightness-110 text-white rounded-lg text-sm font-medium transition-all cursor-pointer border-0 outline-none whitespace-nowrap min-w-fit flex items-center"
                :disabled="
                  isUpdatingDeal || isDeletingDeal || !dealForm.title.trim()
                "
                @click="updateDeal"
              >
                <div
                  v-if="isUpdatingDeal"
                  class="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
                />
                {{ isUpdatingDeal ? "Actualizando..." : "Actualizar Negocio" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>

  <!-- Fondo oscuro -->
  <div
    v-if="showEditDealForm"
    class="fixed inset-0 bg-black/30 z-40"
    @click="closeEditDealForm"
  />

  <!-- Modal de Confirmación de Eliminación -->
  <div
    v-if="showDeleteConfirmation"
    class="fixed inset-0 flex items-center justify-center p-4"
    style="z-index: 9999; background-color: rgba(0, 0, 0, 0.8)"
  >
    <div
      class="bg-white dark:bg-n-solid-3 rounded-lg p-6 w-full max-w-md shadow-lg"
    >
      <div class="flex items-center mb-4">
        <div
          class="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4"
        >
          <svg
            class="w-6 h-6 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
            Eliminar Negocio
          </h3>
          <p class="text-sm text-n-slate-11">
            Esta acción no se puede deshacer
          </p>
        </div>
      </div>

      <div class="mb-6">
        <p class="text-n-slate-12 mb-2">
          ¿Estás seguro de que deseas eliminar el negocio?
        </p>
        <div class="bg-n-slate-9/5 dark:bg-n-solid-2 rounded-lg p-3">
          <p class="font-medium text-n-slate-12 truncate">
            "{{ editingDeal?.title }}"
          </p>
          <div class="text-sm text-n-slate-11 mt-1">
            <span v-if="editingDeal?.amount_cents">
              {{ editingDeal.currency }}
              {{ (editingDeal.amount_cents / 100).toFixed(2) }}
            </span>
            <span v-if="editingDeal?.primary_contact">
              • {{ editingDeal.primary_contact.name }}</span>
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-3">
        <button
          type="button"
          class="px-4 py-2 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all font-medium"
          :disabled="isDeletingDeal"
          @click="hideDeleteDealConfirmation"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg cursor-pointer border-0 outline-none transition-all font-medium flex items-center"
          :disabled="isDeletingDeal"
          @click="deleteDeal"
        >
          <div
            v-if="isDeletingDeal"
            class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
          />
          {{ isDeletingDeal ? "Eliminando..." : "Eliminar Definitivamente" }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal de Crear Actividad -->
  <div
    v-if="showCreateActivityModal"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div
      class="bg-white dark:bg-n-solid-3 rounded-lg p-6 w-[500px] max-w-lg shadow-lg"
    >
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            Nueva {{ getActivityTypeName(activityForm.activity_type) }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-1">
            Para: "{{ selectedDealForActivities?.title }}"
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="closeCreateActivityModal"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Form -->
      <form class="space-y-4" @submit.prevent="createActivity">
        <!-- Tipo de Actividad -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Tipo de Actividad
          </label>
          <select
            v-model="activityForm.activity_type"
            class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          >
            <option value="task">📋 Tarea</option>
            <option value="call">📞 Llamada</option>
            <option value="meeting">🤝 Reunión</option>
            <option value="email">📧 Email</option>
            <option value="note">📝 Nota</option>
          </select>
        </div>

        <!-- Asunto -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Asunto <span class="text-n-ruby-10">*</span>
          </label>
          <input
            v-model="activityForm.subject"
            type="text"
            required
            placeholder="Escribe el asunto de la actividad..."
            class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
            maxlength="100"
          />
          <p class="text-xs text-n-slate-11 mt-1">Máximo 100 caracteres</p>
        </div>

        <!-- Descripción -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Descripción
          </label>
          <textarea
            v-model="activityForm.description"
            rows="4"
            placeholder="Añade detalles sobre la actividad..."
            class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 resize-none"
            maxlength="500"
          />
          <p class="text-xs text-n-slate-11 mt-1">Máximo 500 caracteres</p>
        </div>

        <!-- Fecha de Vencimiento -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Fecha de Vencimiento
          </label>
          <input
            v-model="activityForm.due_at"
            type="datetime-local"
            class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          />
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            class="px-4 py-2 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all"
            :disabled="isCreatingActivity"
            @click="closeCreateActivityModal"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-n-brand hover:brightness-110 text-white rounded-lg cursor-pointer border-0 outline-none transition-all flex items-center"
            :disabled="isCreatingActivity || !activityForm.subject.trim()"
          >
            <div
              v-if="isCreatingActivity"
              class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
            />
            {{ isCreatingActivity ? "Creando..." : "Crear Actividad" }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Modal de Detalles de Actividad -->
  <div
    v-if="showActivityDetailModal"
    class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    @click="closeActivityDetailModal"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-96 max-w-md mx-4"
      @click.stop
    >
      <!-- Header del Modal -->
      <div
        class="flex justify-between items-start p-4"
        style="border-bottom: 1px solid #e5e7eb"
      >
        <div class="flex items-center">
          <span class="text-2xl mr-2">{{
            getActivityTypeIcon(selectedActivity?.activity_type)
          }}</span>
          <h3
            class="text-lg font-semibold text-gray-900 dark:text-white truncate"
          >
            {{ selectedActivity?.subject }}
          </h3>
        </div>
        <button
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          @click="closeActivityDetailModal"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Contenido del Modal -->
      <div class="p-4 space-y-3">
        <div
          class="flex justify-between py-2"
          style="border-bottom: 1px solid #e5e7eb"
        >
          <span class="font-medium text-gray-600 dark:text-gray-400">Tipo:</span>
          <span class="text-gray-500 dark:text-gray-500">{{
            getActivityTypeInSpanish(selectedActivity?.activity_type)
          }}</span>
        </div>

        <div
          v-if="selectedActivity?.due_at"
          class="flex justify-between py-2"
          style="border-bottom: 1px solid #e5e7eb"
        >
          <span class="font-medium text-gray-600 dark:text-gray-400">Vence:</span>
          <span class="text-gray-500 dark:text-gray-500">{{
            formatDate(selectedActivity.due_at)
          }}</span>
        </div>

        <div
          class="flex justify-between py-2"
          style="border-bottom: 1px solid #e5e7eb"
        >
          <span class="font-medium text-gray-600 dark:text-gray-400">Estado:</span>
          <span class="text-gray-500 dark:text-gray-500">
            <span
              v-if="selectedActivity?.completed_at"
              class="text-green-600 dark:text-green-400"
            >
              ✅ Completada
            </span>
            <span v-else class="text-orange-600 dark:text-orange-400">
              ⏳ Pendiente
            </span>
          </span>
        </div>

        <div v-if="selectedActivity?.description" class="pt-3">
          <span class="font-medium text-gray-600 dark:text-gray-400 block mb-2">Descripción:</span>
          <p
            class="text-sm text-gray-500 dark:text-gray-500 whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 p-3 rounded"
            style="border: 1px solid #e5e7eb"
          >
            {{ selectedActivity.description }}
          </p>
        </div>
      </div>

      <!-- Footer del Modal -->
      <div
        class="flex justify-between p-4 bg-gray-50 dark:bg-slate-700 rounded-b-lg"
        style="border-top: 1px solid #e5e7eb"
      >
        <button
          class="flex items-center px-3 py-2 text-sm text-red-600 hover:text-white hover:bg-red-600 dark:text-red-400 dark:hover:text-white dark:hover:bg-red-500 border border-red-300 hover:border-red-600 dark:border-red-400 rounded transition-all duration-150"
          @click="deleteActivityFromModal"
        >
          <svg
            class="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          Eliminar
        </button>

        <div class="flex space-x-2">
          <button
            class="px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white border border-gray-500 hover:border-gray-600 rounded transition-all duration-150"
            @click="closeActivityDetailModal"
          >
            Cerrar
          </button>
          <button
            v-if="!selectedActivity?.completed_at"
            class="flex items-center px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white border border-green-600 hover:border-green-700 rounded transition-all duration-150"
            @click="completeActivityFromModal"
          >
            <svg
              class="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            Completar
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Orquesta el cambio de etapa (drag y modal de edición comparten esta
       misma instancia); muestra el formulario del stage cuando corresponde -->
  <DealStageMover
    ref="dealStageMover"
    @moved="handleDealMoved"
    @form-required="handleDealMoveFormRequired"
    @move-failed="handleDealMoveFailed"
    @cancelled="handleDealMoveCancelled"
  />
</template>

<!-- estilo al final del archivo -->
<style>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
/* Reset del input del buscador mobile: mata la apariencia nativa y, sobre todo,
   el focus ring/borde violeta global que la app aplica a input:focus (por eso
   el !important y los estados de foco explícitos). */
.crm-search-input,
.crm-search-input:focus,
.crm-search-input:focus-visible,
.crm-search-input:focus-within {
  -webkit-appearance: none !important;
  appearance: none !important;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
  border: 0 !important;
  border-radius: 0 !important;
}
</style>
